import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactFlow, { applyEdgeChanges, applyNodeChanges, Background, Controls, MiniMap } from 'reactflow';
import 'reactflow/dist/style.css';
import '../../assets/css/workflow/workflow.css';
import SAModalConfirm from '../common/SAModalConfirm';
import ConsoleDrawer from './ConsoleDrawer';
import NodeConfigModal from './NodeConfigModal';
import NodeConfigSidebar from './NodeConfigSidebar';
import NodePalette, { llmNodes, mcpToolNodes, toolNodes } from './NodePalette';
import WorkflowService from '../../services/api/workflow';
import ClusterService from '../../services/api/cluster';
import { API_RESPONSE } from '../../settings/shared/constants';
import { showNotificationGlobal } from '../common/NotificationProvider';
import EmptyWorkflow from './EmptyWorkflow';
// Import extracted components and utilities
import { validateNodeData, showWorkflowData, createEventSystem, isCustomLlmType, normalizeLlmType } from './workflowUtils';
import { proOptions, nodeTypes, edgeTypes } from './workflowConstants';
import { createValidateWorkflowStructure, createValidateNodeDataContent, createValidateConnectedAppReferences } from './workflowValidation';
import { createBuildWorkflowPayload, isMessageEventWorkflow } from './workflowPayload';

const WORKLOAD_LIVE_POLL_MS = 5000;

/** Map raw pod/workload phase to the labels used by Agent Workflow UI. */
const mapWorkloadStatusLabel = (rawStatus) => {
  const s = String(rawStatus || '').toUpperCase().replace(/[\s_-]/g, '');
  if (!s) return null;
  if (s.includes('STOPPING') || s.includes('DELETING')) return 'Stopping';
  if (s.includes('DELET') || s.includes('STOP') || s.includes('TERMINAT')) return 'Stopped';
  if (
      s.includes('FAIL')
      || s.includes('ERROR')
      || s.includes('CRASH')
      || s.includes('BACKOFF')
      || s.includes('EVICT')
  ) {
    return 'Failed';
  }
  if (s.includes('RUN') || s.includes('READY') || s.includes('SUCCEED') || s.includes('COMPLETED')) {
    return 'Running';
  }
  return null;
};

const extractLiveWorkloadRaw = (res) => {
  if (!res || typeof res !== 'object') return null;
  const listCandidates = [
    res?.page?.content,
    res?.dto?.page?.content,
    res?.pods,
    res?.k8sPodDTOS,
    res?.podDTOS,
  ];
  for (const list of listCandidates) {
    if (Array.isArray(list) && list.length) return list[0];
  }
  const candidates = [res.pod, res.dto, res.k8sPodDTO, res.podDTO, res.data];
  for (const candidate of candidates) {
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) return candidate;
  }
  if (res.podName || res.podStatus || res.namespace || res.sourceId) return res;
  return null;
};

const extractWorkloadPhase = (raw) => {
  if (!raw || typeof raw !== 'object') return '';
  const statusObj = raw.status && typeof raw.status === 'object' ? raw.status : null;
  return (
      raw.podStatus
      || raw.phase
      || raw.podPhase
      || (typeof raw.status === 'string' ? raw.status : null)
      || statusObj?.phase
      || ''
  );
};

const workloadMatchesProject = (raw, projectId) => {
  if (!raw || !projectId) return false;
  const id = String(projectId);
  const candidates = [
    raw.sourceId,
    raw.id,
    raw.projectId,
    raw.projectName,
  ].map((v) => (v == null ? '' : String(v)));
  return candidates.some((c) => c && c === id);
};

const EVENT_ALREADY_RUNNING_CODE = 26001;

const isEventAlreadyRunningError = (res) => {
  if (!res || typeof res !== 'object') return false;
  if (Number(res.errorCode) === EVENT_ALREADY_RUNNING_CODE) return true;
  const text = String(res.errorMessage || res.message || '');
  return /already\s*running/i.test(text);
};

const executeErrorMessage = (res, fallback = 'Unknown error occurred') => {
  if (!res || typeof res !== 'object') return fallback;
  return res.errorMessage || res.message || fallback;
};

const applyEvaluationToPayload = (payload, evaluation) => {
  if (!payload || !evaluation || typeof evaluation !== 'object') return payload;
  payload.evaluate = Boolean(evaluation.evaluate);
  if (payload.evaluate) {
    payload.evaluationType = evaluation.evaluationType || 'agent';
    if (Array.isArray(evaluation.expected) && evaluation.expected.length > 0) {
      payload.expected = evaluation.expected;
    }
    if (evaluation.metricId) {
      payload.metricId = evaluation.metricId;
    }
  }
  return payload;
};

const evaluationStorageKey = (projectId) =>
    projectId ? `sa-evaluation-settings:${projectId}` : null;

const readStoredEvaluation = (projectId) => {
  const key = evaluationStorageKey(projectId);
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const storeEvaluationSettings = (projectId, evaluation) => {
  const key = evaluationStorageKey(projectId);
  if (!key || !evaluation) return;
  try {
    sessionStorage.setItem(key, JSON.stringify(evaluation));
  } catch {
    //  private mode
  }
};

const evaluationFromWorkflowInfo = (info) => {
  const wf = info?.workFlowTemplateDTO || info || {};
  const evaluate = typeof wf.evaluate === 'boolean' ? wf.evaluate : undefined;
  if (evaluate === undefined) return null;
  const fields = { evaluate };
  if (evaluate) {
    fields.evaluationType = wf.evaluationType || 'agent';
    if (Array.isArray(wf.expected) && wf.expected.length > 0) {
      fields.expected = wf.expected;
    }
    const metricId = wf.metricId || wf.metricsId || wf.metricCollectionId || wf.metric?.id || null;
    if (metricId) fields.metricId = metricId;
  }
  return fields;
};

export default function DrawWorkFlow({ appInfo, chatVisible = false, chatWidth = 400 }) {
  const reactFlowWrapper = useRef(null);
  const eventSystem = useRef(createEventSystem());
  const rfInstanceRef = useRef(null);

  const workfowAppInfo = useMemo(() => {
    const normalized = showWorkflowData(appInfo);
    return normalized;
  }, [appInfo]);

  const [nodes, setNodes] = useState(() => {
    if (workfowAppInfo && workfowAppInfo?.appConfigDTOList && workfowAppInfo?.appConfigDTOList?.length > 0) {
      return workfowAppInfo?.appConfigDTOList[0]?.nodes || [];
    }
    return [];
  });

  // Initialize with empty state to reduce memory overhead
  const [edges, setEdges] = useState(() => {
    if (workfowAppInfo && workfowAppInfo?.appConfigDTOList && workfowAppInfo?.appConfigDTOList?.length > 0) {
      return workfowAppInfo?.appConfigDTOList[0]?.edges || [];
    }
    return [];
  });
  const [selectedNode, setSelectedNode] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rfInstance, setRfInstance] = useState(null);
  const [showSWConfirm, setShowSWConfirm] = useState(false);
  const [showDeleteNodeConfirm, setShowDeleteNodeConfirm] = useState(false);
  const [pendingDeleteNode, setPendingDeleteNode] = useState(null); // { nodeId, nodeType }
  const [unconnectedNodeIds, setUnconnectedNodeIds] = useState([]);
  const [workflowName, setWorkflowName] = useState(workfowAppInfo?.name || '');
  const [workflowDescription, setWorkflowDescription] = useState(workfowAppInfo?.description || workfowAppInfo?.projectInfo?.description || '');
  const [invalidAgentIds, setInvalidAgentIds] = useState([]);
  const [showNodeConfigModal, setShowNodeConfigModal] = useState(false);
  const [configModalData, setConfigModalData] = useState({});
  const navigate = useNavigate();
  const { id: projectIdFromRoute } = useParams(); // Get id from route params (e.g., /project/:id)
  const urlParams = new URLSearchParams(window.location.search);
  const projectIdFromSearch = urlParams.get('projectId') || urlParams.get('id');
  const projectIdFromAppInfo = workfowAppInfo?.id;

  // Priority: route param > search param > appInfo
  const initialProjectId = projectIdFromRoute || projectIdFromSearch || projectIdFromAppInfo;
  const [ProjectID, setProjectId] = useState(initialProjectId);

  // Update ProjectID when URL params or appInfo changes
  useEffect(() => {
    const currentUrlParams = new URLSearchParams(window.location.search);
    const currentProjectIdFromSearch = currentUrlParams.get('projectId') || currentUrlParams.get('id');
    const currentProjectIdFromAppInfo = workfowAppInfo?.id;

    // Priority: route param > search param > appInfo
    const newProjectId = projectIdFromRoute || currentProjectIdFromSearch || currentProjectIdFromAppInfo;
    if (newProjectId && newProjectId !== ProjectID) {
      setProjectId(newProjectId);
    } else if (!newProjectId && ProjectID) {
      // Clear ProjectID if no valid projectId found
      setProjectId(null);
    }
  }, [projectIdFromRoute, workfowAppInfo?.id, ProjectID]);

  // Update nodes and edges when appInfo/workflow data changes
  useEffect(() => {
    if (!workfowAppInfo) return;

    let newNodes = [];
    let newEdges = [];

    // Check multiple possible data structures
    if (workfowAppInfo?.appConfigDTOList && workfowAppInfo?.appConfigDTOList?.length > 0) {
      const workflowData = workfowAppInfo.appConfigDTOList[0];
      newNodes = workflowData?.nodes || [];
      newEdges = workflowData?.edges || [];
    } else if (workfowAppInfo?.nodes || workfowAppInfo?.edges) {
      // Direct nodes/edges in response
      newNodes = workfowAppInfo.nodes || [];
      newEdges = workfowAppInfo.edges || [];
    }

    // Update nodes if we have new nodes
    if (newNodes.length > 0) {
      suppressWorkflowChangeRef.current = true;
      setNodes(newNodes);
      // Initialize saved state when loading existing workflow
      savedNodesRef.current = JSON.parse(JSON.stringify(newNodes));
      // Update ReactFlow instance if it exists
      if (rfInstance) {
        rfInstance.setNodes(newNodes);
      }
    }

    // Update edges if we have new edges
    if (newEdges.length > 0) {
      suppressWorkflowChangeRef.current = true;
      setEdges(newEdges);
      // Initialize saved state when loading existing workflow
      savedEdgesRef.current = JSON.parse(JSON.stringify(newEdges));
      // Update ReactFlow instance if it exists
      if (rfInstance) {
        rfInstance.setEdges(newEdges);
      }
    }

    // Loading workflow data should not mark it dirty; re-enable dirty tracking next tick.
    if (newNodes.length > 0 || newEdges.length > 0) {
      markWorkflowClean();
      setTimeout(() => {
        suppressWorkflowChangeRef.current = false;
      }, 0);
    }

    // Update workflow name and description if available
    if (workfowAppInfo.name) {
      setWorkflowName(workfowAppInfo.name || '');
    }
    if (workfowAppInfo.description || workfowAppInfo.projectInfo?.description) {
      setWorkflowDescription(workfowAppInfo.description || workfowAppInfo.projectInfo?.description || '');
    }
  }, [workfowAppInfo, rfInstance]);

  useEffect(() => {
    const fromInfo = evaluationFromWorkflowInfo(workfowAppInfo);
    const stored = readStoredEvaluation(ProjectID);
    const next =
        (stored?.evaluate === true ? stored : null) ||
        fromInfo ||
        stored ||
        null;
    if (next) {
      evaluationSettingsRef.current = next;
    }
  }, [workfowAppInfo, ProjectID]);

  // Also update nodes/edges when rfInstance becomes available (in case data was loaded before instance)
  useEffect(() => {
    if (!rfInstance) return;

    // Check if we have nodes/edges in state but ReactFlow instance doesn't have them
    const currentRfNodes = rfInstance.getNodes();
    const currentRfEdges = rfInstance.getEdges();

    if (nodes.length > 0 && currentRfNodes.length === 0) {
      suppressWorkflowChangeRef.current = true;
      rfInstance.setNodes(nodes);
    }
    if (edges.length > 0 && currentRfEdges.length === 0) {
      suppressWorkflowChangeRef.current = true;
      rfInstance.setEdges(edges);
    }
    if ((nodes.length > 0 && currentRfNodes.length === 0) || (edges.length > 0 && currentRfEdges.length === 0)) {
      setTimeout(() => {
        suppressWorkflowChangeRef.current = false;
      }, 0);
    }
  }, [rfInstance, nodes, edges]);

  // Keep nodesRef/edgesRef updated with current graph for event handlers
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  const [showPalette, setShowPalette] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Console drawer state
  const [consoleDrawerOpen, setConsoleDrawerOpen] = useState(false);
  const [consoleNodeData, setConsoleNodeData] = useState(null);
  const [focusConsoleInput, setFocusConsoleInput] = useState(false);
  const [expandConsoleOnOpen, setExpandConsoleOnOpen] = useState(false);
  const [isWorkflowRunning, setIsWorkflowRunning] = useState(false);
  const isWorkflowRunningRef = useRef(false);
  isWorkflowRunningRef.current = isWorkflowRunning;
  const podStopInFlightRef = useRef(false);
  const podStopRequestedRef = useRef(false);
  const dockerStopResetRef = useRef(false);
  const podStatusDetailRef = useRef(null);
  const workloadWatchIdRef = useRef(null);
  const workloadProbedProjectRef = useRef(null);
  const [workloadWatchId, setWorkloadWatchId] = useState(null);
  const [runningAgentId, setRunningAgentId] = useState(null);
  // Node IDs currently highlighted green (executing) - used to animate incoming edges
  const [executingNodeIds, setExecutingNodeIds] = useState(() => new Set());

  // Sync executingNodeIds from node-highlight (green) and node-highlight-stop-all events
  useEffect(() => {
    const onHighlight = (e) => {
      const { nodeId, color } = e.detail || {};
      if (color === 'green' && nodeId) {
        setExecutingNodeIds((prev) => new Set(prev).add(nodeId));
      }
    };
    const onStopAll = () => setExecutingNodeIds(new Set());
    window.addEventListener('node-highlight', onHighlight);
    window.addEventListener('node-highlight-stop-all', onStopAll);
    return () => {
      window.removeEventListener('node-highlight', onHighlight);
      window.removeEventListener('node-highlight-stop-all', onStopAll);
    };
  }, []);
  const consoleQueryRef = useRef(''); // Ref to store current query for immediate access
  // Map to store uid to node.id mapping for agent highlighting
  const agentUidToNodeIdMap = useRef(new Map());

  // Ref to prevent duplicate createWorkflow calls
  const isCreatingWorkflowRef = useRef(false);

  // Refs to store last saved nodes and edges for change detection
  const savedNodesRef = useRef(null);
  const savedEdgesRef = useRef(null);
  // Ref to store last saved appConfig for change detection
  const savedAppConfigRef = useRef(null);
  const evaluationSettingsRef = useRef(
      evaluationFromWorkflowInfo(workfowAppInfo) || readStoredEvaluation(initialProjectId)
  );

  // Workflow changes tracking
  const workflowChangesRef = useRef(false);
  const suppressWorkflowChangeRef = useRef(false);
  const markWorkflowChanged = useCallback(() => {
    if (suppressWorkflowChangeRef.current) return;
    if (workflowChangesRef.current) return;
    workflowChangesRef.current = true;
    window.dispatchEvent(
        new CustomEvent('workflow-simple-changed', { detail: { isChanged: true } })
    );
  }, []);
  const markWorkflowClean = useCallback(() => {
    if (!workflowChangesRef.current) return;
    workflowChangesRef.current = false;
    window.dispatchEvent(
        new CustomEvent('workflow-simple-changed', { detail: { isChanged: false } })
    );
  }, []);

  // Ref to store current nodes/edges for event handlers (avoids stale closure issues)
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);

  // Helper function to set alert with type and optional tooltip
  const showAlert = useCallback((message, type = 'error', tooltip = '') => {
    const displayMessage = tooltip ? `${message} (${tooltip})` : message;
    showNotificationGlobal(displayMessage, type, 'bottomCenter');
  }, []);

  // Create validation functions using factory functions
  const validateWorkflowStructure = useMemo(
      () => createValidateWorkflowStructure(showAlert, setUnconnectedNodeIds, setInvalidAgentIds),
      [showAlert]
  );

  // Always read the live toolNodes export so validation isn't stuck on an empty snapshot
  const getToolNodes = useCallback(() => toolNodes, []);

  const validateNodeDataContent = useMemo(
      () => createValidateNodeDataContent(setSelectedNode, setDrawerOpen, setNodes, getToolNodes),
      [getToolNodes]
  );

  const validateConnectedAppReferences = useMemo(
      () =>
          createValidateConnectedAppReferences(
              setSelectedNode,
              setDrawerOpen,
              setNodes,
              getToolNodes,
              WorkflowService.getAuthProvider
          ),
      [getToolNodes]
  );

  // Create payload builder function
  const buildWorkflowPayload = useMemo(
      () => createBuildWorkflowPayload(ProjectID, agentUidToNodeIdMap, consoleQueryRef, workfowAppInfo?.name),
      [ProjectID, workfowAppInfo?.name]
  );

  // This is the function to use for workflow validation
  const validateWorkflow = useCallback(async (currentNodes, currentEdges, onValidationError) => {
    // First validate workflow structure (topology/connections)
    if (!validateWorkflowStructure(currentNodes, currentEdges)) {
      return false;
    }

    // Then validate node data content (LLM models, agent properties)
    if (!validateNodeDataContent(currentNodes, currentEdges, onValidationError)) {
      return false;
    }

    // Ensure oauth tool configs still reference existing connected apps
    if (!(await validateConnectedAppReferences(currentNodes, onValidationError))) {
      return false;
    }

    return true;
  }, [validateWorkflowStructure, validateNodeDataContent, validateConnectedAppReferences]);

  const validateWorkflowRef = useRef(validateWorkflow);
  useEffect(() => {
    validateWorkflowRef.current = validateWorkflow;
  }, [validateWorkflow]);

  const isSavingRef = useRef(false);
  const saveWorkflowRef = useRef(null);

  // Helper function to extract projectId from API response and update state/URL
  const updateProjectIdFromResponse = useCallback((res, checkExisting = false) => {
    const newProjectId = res?.projectDTO?.id || res?.projectId || res?.data?.projectDTO?.id || res?.data?.projectId || res?.id;
    if (newProjectId && (!checkExisting || !ProjectID)) {
      setProjectId(newProjectId);
      window.dispatchEvent(new CustomEvent('workflow-project-id', { detail: { projectId: newProjectId } }));

      // Update URL if we're on agent page without projectId
      const currentUrl = new URL(window.location.href);
      if (currentUrl.pathname === '/app/agent' && !currentUrl.searchParams.get('projectId')) {
        const newUrl = `/app/agent?id=${newProjectId}`;
        window.history.replaceState(null, '', newUrl);
      }
    }
    return newProjectId;
  }, [ProjectID]);
  // Drag-and-drop handlers
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
      (event) => {
        event.preventDefault();
        markWorkflowChanged();
        const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
        const type = event.dataTransfer.getData('application/reactflow');

        // Add comprehensive error handling and validation for JSON parsing
        let nodeData;
        try {
          const nodeDataStr = event.dataTransfer.getData('nodeData');
          if (!nodeDataStr || typeof nodeDataStr !== 'string') {
            showAlert('Invalid node data: Missing or invalid node configuration');
            return;
          }

          // Parse JSON directly - no sanitization needed as it comes from our own code
          // Sanitization would break JSON structure (quotes, etc.)
          nodeData = JSON.parse(nodeDataStr);

          // Validate the parsed data structure
          if (!validateNodeData(nodeData)) {
            showAlert('Invalid node data: Invalid data structure');
            return;
          }

          // If it's a tool node, make sure we have the srcPath
          if (type === 'config' && nodeData.configType === 'tool') {
            // For MCP tools, srcPath is already included in nodeData
            // For regular tools, we need to look it up from toolNodes
            if (!nodeData.srcPath && nodeData.handlerName) {
              const toolConfig = toolNodes && toolNodes.length > 0
                  ? toolNodes.find(t => t.handlerName === nodeData.handlerName)
                  : null;
              if (toolConfig) {
                nodeData = {
                  ...nodeData,
                  srcPath: toolConfig.srcPath,
                  icon: nodeData.icon || toolConfig.icon,
                  iconColor: nodeData.iconColor || toolConfig.iconColor,
                  color: nodeData.color || toolConfig.color,
                  attributes: nodeData.attributes || toolConfig.attributes || {}
                };
              }
            }
            // Ensure attributes exists
            if (!nodeData.attributes) {
              nodeData.attributes = {};
            }
          }
        } catch (error) {
          console.error('JSON parsing error:', error);
          console.error('Failed to parse nodeData:', event.dataTransfer.getData('nodeData'));
          console.error('Node type:', type);
          showAlert('Invalid node data: Unable to parse configuration');
          return;
        }

        if (!type) return;
        const position = rfInstance.project({
          x: event.clientX - reactFlowBounds.left,
          y: event.clientY - reactFlowBounds.top,
        });

        // Determine node type and title based on label
        let nodeType = type;
        if (type === 'agent' && nodeData?.label === 'Task Agent') {
          nodeType = 'task';
          nodeData.title = 'Task Agent';
        } else if (nodeData?.label === "Extract") {
          nodeData.title = 'Extract Agent';
        } else {
          nodeData.title = nodeData.title || 'Agent';
        }

        let data = nodeData;
        let newId = `${nodeType}-${+new Date()}`;
        if (type === 'agent' || nodeType === 'task') {
          data = {
            ...nodeData,
            tools: [],
            llm: '',
            memory: false,
            maxRetry: 1, // Set default maxRetry value
            onConfigNodeAdd: (configType, value) => handleConfigNodeAdd(newId, configType, value),
            onChange: (data) => handleNodeConfigChange(data),
            selected: false,
          };
        }
        const newNode = {
          id: newId,
          type: nodeType,
          position,
          data,
        };
        setNodes((nds) => nds.concat(newNode));
        // Automatically create and connect predefined LLM and tool nodes for agent
        // This creates an OpenAI LLM node and the first available tool node when an agent is dropped
        if (type === 'agent' && nodeData.label === 'Extract') {
          const timestamp = Date.now();

          // Calculate positions for config nodes (LLM on left, tool on right)
          const llmPosition = { x: position.x - 80, y: position.y + 120 };
          const toolPosition = { x: position.x + 80, y: position.y + 120 };
          // Create OpenAI LLM node
          const openaiNode = {
            id: `config-llm-openai-${timestamp}`,
            type: 'config',
            position: llmPosition,
            data: {
              configType: 'llm',
              llmType: 'openai',
              displayName: 'OpenAI',
              model: '',
              apiKey: '',
              baseUrl: '',
              apiVersion: '',
              asyncMode: false,
              embedModel: '',
              iconColor: '#1976d2'
            },
          };

          const extractTool = toolNodes && toolNodes.length > 0 ? toolNodes.find(t => t.handlerName === 'ExtractAIHandler'):"";

          // Create tool node using the first available tool
          const toolNode = {
            id: `config-tool-${extractTool?.handlerName}-${timestamp}`,
            type: 'config',
            position: toolPosition,
            data: {
              configType: 'tool',
              handlerName: extractTool?.handlerName,
              displayName: extractTool?.displayName,
              srcPath: extractTool?.srcPath,
              icon: extractTool?.icon,
              iconColor: extractTool?.iconColor,
              color: extractTool?.color,
              attributes: []
            },
          };

          // Add the config nodes
          setNodes((nds) => nds.concat([openaiNode, toolNode]));

          // Create edges connecting agent to LLM and tool
          const llmEdge = {
            id: `${newId}-${openaiNode.id}`,
            source: newId,
            target: openaiNode.id,
            type: 'dashed',
            sourceHandle: 'chatModel',
            targetHandle: 'top',
            data: {
              sourceType: 'agent',
              targetType: 'config'
            }
          };

          const toolEdge = {
            id: `${newId}-${toolNode.id}`,
            source: newId,
            target: toolNode.id,
            type: 'dashed',
            sourceHandle: 'tool',
            targetHandle: 'top',
            data: {
              sourceType: 'agent',
              targetType: 'config'
            }
          };
          setEdges((eds) => eds.concat([llmEdge, toolEdge]));
        }

        // Automatically open sidebar for agent nodes, task nodes, tools, and LLMs
        if (type === 'agent' || nodeType === 'task' ||
            (type === 'config' && (data.configType === 'tool' || data.configType === 'llm'))) {
          setSelectedNode(newNode);
        }
      },
      [rfInstance, nodes]
  );

  // Memoize agent connections computation to avoid expensive recalculations
  const agentConnections = useMemo(() => {
    const connections = {};
    nodes.forEach(agent => {
      if (agent.type === 'agent' || agent.type === 'task') {
        const connected = { tool: [], llm: false, memory: false };
        nodes.forEach(n => {
          if (n.type === 'config') {
            if (n.data.configType === 'tool' && edges.some(e => e.source === agent.id && e.target === n.id)) {
              connected.tool.push(n.data.handlerName);
            }
            if (n.data.configType === 'llm' && edges.some(e => e.source === agent.id && e.target === n.id)) {
              connected.llm = true;
            }
            if (n.data.configType === 'memory' && edges.some(e => e.source === agent.id && e.target === n.id)) {
              connected.memory = true;
            }
          }
        });
        connections[agent.id] = connected;
      }
    });
    return connections;
  }, [nodes, edges]);

  const onEdgesChange = useCallback((changes) => {
    if (suppressWorkflowChangeRef.current) {
      setEdges((eds) => applyEdgeChanges(changes, eds));
      return;
    }
    // Check for edge deletions that involve extract tools
    const edgeDeletions = changes.filter(change => change.type === 'remove');

    for (const deletion of edgeDeletions) {
      const edgeToDelete = edges.find(edge => edge.id === deletion.id);
      if (edgeToDelete) {
        // Find the source agent node
        const sourceAgent = nodes.find(node => node.id === edgeToDelete.source && node.type === 'agent');
        // Find the target tool node
        const targetTool = nodes.find(node => node.id === edgeToDelete.target && node.type === 'config' && node.data.configType === 'tool');

        // Check if this is an extract agent trying to delete an extract tool
        if (sourceAgent && targetTool &&
            sourceAgent.data.label === 'Extract' &&
            targetTool.data.handlerName === 'ExtractAIHandler') {
          return;
        }
      }
    }

    setEdges((eds) => {
      const updatedEdges = applyEdgeChanges(changes, eds);

      // Ensure all edges have sourceType and targetType information
      const processedEdges = updatedEdges?.map(edge => {
        if (!edge.data?.sourceType || !edge.data?.targetType) {
          const sourceNode = nodes.find(n => n.id === edge.source);
          const targetNode = nodes.find(n => n.id === edge.target);
          return {
            ...edge,
            data: {
              ...edge.data,
              sourceType: sourceNode?.type,
              targetType: targetNode?.type
            }
          };
        }
        return edge;
      });

      // Update agent connections after edge changes - recalculate connections
      setNodes((nds) => {
        // Recalculate agent connections based on updated edges
        const agentConnections = {};
        nds.forEach(agent => {
          if (agent.type === 'agent' || agent.type === 'task') {
            const connected = { tool: [], llm: false, memory: false };
            nds.forEach(n => {
              if (n.type === 'config') {
                if (n.data.configType === 'tool' && processedEdges.some(e => e.source === agent.id && e.target === n.id)) {
                  connected.tool.push(n.data.handlerName);
                }
                if (n.data.configType === 'llm' && processedEdges.some(e => e.source === agent.id && e.target === n.id)) {
                  connected.llm = true;
                }
                if (n.data.configType === 'memory' && processedEdges.some(e => e.source === agent.id && e.target === n.id)) {
                  connected.memory = true;
                }
              }
            });
            agentConnections[agent.id] = connected;
          }
        });

        // Check for task nodes that lost their tool connection and clear methods
        const taskNodesThatLostTool = [];
        nds.forEach(n => {
          if (n.type === 'task') {
            const hadToolBefore = n.data?.connectedConfig?.tool?.length > 0;
            const hasToolNow = agentConnections[n.id]?.tool?.length > 0;
            if (hadToolBefore && !hasToolNow) {
              taskNodesThatLostTool.push(n.id);
            }
          }
        });

        return nds.map(n => {
          if (n.type === 'agent' || n.type === 'task') {
            const newData = {
              ...n.data,
              onConfigNodeAdd: (type, value) => handleConfigNodeAdd(n.id, type, value),
              onChange: (data) => handleNodeConfigChange(data),
              selected: selectedNode && selectedNode.id === n.id,
              connectedConfig: agentConnections[n.id] || { tool: [], llm: false, memory: false },
            };

            // Clear methods if tool was disconnected
            if (n.type === 'task' && taskNodesThatLostTool.includes(n.id)) {
              newData.availableMethods = [];
              newData.methodDescriptions = {};
            }

            return {
              ...n,
              data: newData
            };
          }
          return n;
        });
      });

      return updatedEdges;
    });
  }, [edges, nodes, selectedNode]);

  const handleNodeConfigChange = (data) => {
    markWorkflowChanged();
    // Make a mutable copy to avoid direct state mutation.
    const newData = { ...data };

    if (selectedNode) {
      const nodeType = selectedNode.type;
      const configType = selectedNode.data?.configType;

      // Clear error messages in real-time as the user types.
      if (nodeType === 'config' && configType === 'llm') {
        if (newData.errorMessage === 'Model name is required' && newData.model) {
          newData.errorMessage = undefined;
        }
      }

      if (nodeType === 'agent') {
        if (newData.errorMessage === 'role' && newData.role) {
          newData.errorMessage = undefined;
        }
        if (newData.errorMessage === 'goal' && newData.goal) {
          newData.errorMessage = undefined;
        }
      }
    }

    setNodes((nds) => {
      const updated = nds.map((n) =>
          n.id === selectedNode.id ? { ...n, data: newData } : n
      );
      // Call onNodesChange to refresh connectedConfig
      onNodesChange([]);
      return updated;
    });
    setSelectedNode((n) => n && { ...n, data: newData });
  };

  // Add config node when selected from AgentNode icon
  const handleConfigNodeAdd = useCallback((agentId, type, value) => {
    markWorkflowChanged();
    // Prevent duplicate config nodes for the same agent/type/value
    const existing = nodes.find(
        n => n.type === 'config' && n.data.configType === type && n.data[`${type}Type`] === value && edges.some(e => e.source === agentId && e.target === n.id)
    );
    if (existing) return;
    // Position below agent, offset horizontally by type
    const agentNode = nodes.find(n => n.id === agentId);
    if (!agentNode) return;
    let offsetX = 0;
    if (type === 'llm') offsetX = -60;
    if (type === 'tool') offsetX = 60 * (nodes.filter(n => n.type === 'config' && n.data.configType === 'tool' && edges.some(e => e.source === agentId && e.target === n.id)).length + 1);
    if (type === 'memory') offsetX = 0;
    const newId = `config-${type}-${value}-${+new Date()}`;

    // Find the tool configuration from toolNodes if it's a tool
    const toolConfig = type === 'tool' ? toolNodes.find(t => t.handlerName === value) : null;

    const newNode = {
      id: newId,
      type: 'config',
      position: { x: agentNode.position.x + offsetX, y: agentNode.position.y + 100 },
      data: {
        configType: type,
        [`${type}Type`]: value,
        displayName: (type === 'tool' ? toolConfig?.displayName : type === 'llm' ? llmNodes.find(l => l.llmType === value)?.displayName : 'Memory') || value,
        // Add initial data for LLM config
        ...(type === 'llm' ? {
          llmType: value,
          model: '',
          apiKey: '',
          baseUrl: '',
          apiVersion: '',
          asyncMode: '',
          embedModel: ''
        } : {}),
        // Add srcPath for tools
        ...(type === 'tool' ? {
          handlerName: value,
          srcPath: toolConfig?.srcPath || null,
          attributes: []
        } : {})
      },
    };
    const newEdge = {
      id: `${agentId}-${newId}`,
      source: agentId,
      target: newId,
      type: 'dashed',
      data: {
        sourceType: 'agent',
        targetType: 'config'
      }
    };
    setNodes(nds => nds.concat(newNode));
    setEdges(eds => eds.concat(newEdge));
  }, [nodes, edges]);

  const onNodesChange = useCallback((changes) => {
    if (suppressWorkflowChangeRef.current) {
      setNodes((nds) => applyNodeChanges(changes, nds));
      return;
    }

    const hasMeaningfulChange =
        Array.isArray(changes) &&
        changes.some((c) => {
          if (!c || !c.type) return false;
          if (c.type === 'select') return false;
          if (c.type === 'dimensions') return false;
          if (c.type === 'position') {
            // position changes only matter when a position is actually present (drag/move)
            return Boolean(c.position);
          }
          return true; // add/remove/reset/etc.
        });
    if (hasMeaningfulChange) markWorkflowChanged();
    // Check for node deletions that involve extract tool nodes
    const nodeDeletions = changes.filter(change => change.type === 'remove');

    for (const deletion of nodeDeletions) {
      const nodeToDelete = nodes.find(node => node.id === deletion.id);
      if (nodeToDelete && nodeToDelete.type === 'config' && nodeToDelete.data.configType === 'tool' &&
          nodeToDelete.data.handlerName === 'ExtractAIHandler') {
        // Check if this extract tool is connected to any extract agent
        const connectedToExtractAgent = edges.some(edge =>
            edge.target === deletion.id &&
            nodes.some(agent =>
                agent.id === edge.source &&
                agent.type === 'agent' &&
                agent.data.label === 'Extract'
            )
        );

        if (connectedToExtractAgent) {
          showNotificationGlobal('Cannot delete the Extract tool. It is required for the Extract agent', 'error');
          return;
        }
      }
    }

    setNodes((nds) => {
      // ─────────────────────────────────────────────
      // Group-drag support:
      // When an Agent/Task is dragged, move its connected Tool/LLM config nodes
      // by the same delta to avoid stretched edges.
      // ─────────────────────────────────────────────
      const dragPositionChanges = changes.filter(
          (c) => c.type === 'position' && c.dragging !== undefined
      );

      let augmentedChanges = changes;
      if (dragPositionChanges.length > 0) {
        const nodeById = new Map(nds.map((n) => [n.id, n]));
        const extra = [];

        for (const ch of dragPositionChanges) {
          const parent = nodeById.get(ch.id);
          if (!parent) continue;
          if (!(parent.type === 'agent' || parent.type === 'task')) continue;
          if (!ch.position) continue;

          const dx = ch.position.x - (parent.position?.x ?? 0);
          const dy = ch.position.y - (parent.position?.y ?? 0);
          if (dx === 0 && dy === 0) continue;

          // Move only connected Tool/LLM config nodes
          const connectedConfigIds = edges
              .filter((e) => e.source === parent.id)
              .map((e) => e.target)
              .filter((targetId) => {
                const n = nodeById.get(targetId);
                return (
                    n &&
                    n.type === 'config' &&
                    (n.data?.configType === 'tool' || n.data?.configType === 'llm')
                );
              });

          for (const cid of connectedConfigIds) {
            const cn = nodeById.get(cid);
            if (!cn) continue;
            extra.push({
              id: cid,
              type: 'position',
              position: {
                x: (cn.position?.x ?? 0) + dx,
                y: (cn.position?.y ?? 0) + dy,
              },
              dragging: ch.dragging,
            });
          }
        }

        if (extra.length > 0) augmentedChanges = [...changes, ...extra];
      }

      // Apply the changes first to get updated nodes
      const updatedNodes = applyNodeChanges(augmentedChanges, nds);

      // Remove edges that are connected to deleted nodes
      const deletedNodeIds = changes
          .filter(change => change.type === 'remove')
          .map(change => change.id);

      setEdges((eds) => {
        const updatedEdges = eds.filter(edge =>
            !deletedNodeIds.includes(edge.source) && !deletedNodeIds.includes(edge.target)
        );

        // Compute connected config for each agent using updated nodes and edges
        const agentConnections = {};
        updatedNodes.forEach(agent => {
          if (agent.type === 'agent' || agent.type === 'task') {
            const connected = { tool: [], llm: false, memory: false };
            updatedNodes.forEach(n => {
              if (n.type === 'config') {
                if (n.data.configType === 'tool' && updatedEdges.some(e => e.source === agent.id && e.target === n.id)) {
                  connected.tool.push(n.data.handlerName);
                }
                if (n.data.configType === 'llm' && updatedEdges.some(e => e.source === agent.id && e.target === n.id)) {
                  connected.llm = true;
                }
                if (n.data.configType === 'memory' && updatedEdges.some(e => e.source === agent.id && e.target === n.id)) {
                  connected.memory = true;
                }
              }
            });
            agentConnections[agent.id] = connected;
          }
        });

        // Update nodes with new connections
        setNodes((finalNodes) =>
            finalNodes.map(n => {
              if (n.type === 'agent' || n.type === 'task') {
                return {
                  ...n,
                  data: {
                    ...n.data,
                    onConfigNodeAdd: (type, value) => handleConfigNodeAdd(n.id, type, value),
                    onChange: (data) => handleNodeConfigChange(data),
                    selected: selectedNode && selectedNode.id === n.id,
                    connectedConfig: agentConnections[n.id] || { tool: [], llm: false, memory: false },
                  }
                };
              }
              return n;
            })
        );

        return updatedEdges;
      });

      return updatedNodes;
    });
  }, [selectedNode, edges, nodes, handleConfigNodeAdd, handleNodeConfigChange]);

  const onConnect = useCallback((params) => {
    markWorkflowChanged();
    // Get source and target node types
    const sourceNode = nodes.find(n => n.id === params.source);
    const targetNode = nodes.find(n => n.id === params.target);

    const newEdge = {
      ...params,
      type: "line",
      strokeWidth: 1,
      opacity: 0.8,
      id: `e-${params.source}-${params.target}`,
      data: {
        ...params.data,
        sourceType: sourceNode?.type,
        targetType: targetNode?.type
      }
    };

    // Update edges first
    setEdges((eds) => {
      const updatedEdges = [...eds, newEdge];

      // Ensure all edges have sourceType and targetType information
      const processedEdges = updatedEdges.map(edge => {
        if (!edge.data?.sourceType || !edge.data?.targetType) {
          const sourceNode = nodes.find(n => n.id === edge.source);
          const targetNode = nodes.find(n => n.id === edge.target);
          return {
            ...edge,
            data: {
              ...edge.data,
              sourceType: sourceNode?.type,
              targetType: targetNode?.type
            }
          };
        }
        return edge;
      });

      // Update nodes immediately with new connections
      setNodes((nds) => {
        // Compute connected config for each agent using updated edges
        const agentConnections = {};
        nds.forEach(agent => {
          if (agent.type === 'agent' || agent.type === 'task') {
            const connected = { tool: [], llm: false, memory: false };
            nds.forEach(n => {
              if (n.type === 'config') {
                if (n.data.configType === 'tool' && processedEdges.some(e => e.source === agent.id && e.target === n.id)) {
                  connected.tool.push(n.data.handlerName);
                }
                if (n.data.configType === 'llm' && processedEdges.some(e => e.source === agent.id && e.target === n.id)) {
                  connected.llm = true;
                }
                if (n.data.configType === 'memory' && processedEdges.some(e => e.source === agent.id && e.target === n.id)) {
                  connected.memory = true;
                }
              }
            });
            agentConnections[agent.id] = connected;
          }
        });

        return nds.map(n => {
          if (n.type === 'agent' || n.type === 'task') {
            return {
              ...n,
              data: {
                ...n.data,
                onConfigNodeAdd: (type, value) => handleConfigNodeAdd(n.id, type, value),
                onChange: (data) => handleNodeConfigChange(data),
                selected: selectedNode && selectedNode.id === n.id,
                connectedConfig: agentConnections[n.id] || { tool: [], llm: false, memory: false },
              }
            };
          }
          return n;
        });
      });

      return processedEdges;
    });
  }, [nodes, selectedNode, handleConfigNodeAdd, handleNodeConfigChange]);

  const onEdgeClick = useCallback((event, edge) => {
    // Select the clicked edge
    setEdges((eds) => eds.map(e =>
        e.id === edge.id ? { ...e, selected: true } : { ...e, selected: false }
    ));
  }, []);

  const onNodeClick = useCallback((event, node) => {
    if (node?.type === 'output') return;
    // Helper function to get provider for a node
    const getProviderForNode = (node) => {
      // For tool config nodes, get provider from tool config
      if (node.type === 'config' && node.data.configType === 'tool') {
        // Check MCP tools first
        if (node.data.key) {
          const mcpToolConfig = mcpToolNodes.find(t => t.key === node.data.key);
          return mcpToolConfig?.provider || null;
        }
        // Check regular tools
        if (node.data.handlerName) {
          const toolConfig = toolNodes.find(t => t.handlerName === node.data.handlerName);
          return toolConfig?.provider || null;
        }
      }
      // For other node types, return null if no provider in node.data
      return node.data?.provider || null;
    };

    // Helper function to set selected node with provider
    const setSelectedNodeWithProvider = (nodeToSelect, additionalData = {}) => {
      const provider = getProviderForNode(nodeToSelect);
      setSelectedNode({
        ...nodeToSelect,
        data: {
          ...nodeToSelect.data,
          provider: provider,
          ...additionalData
        }
      });
    };

    // Handle task node double-click - fetch methods from connected tool
    if (node.type === 'task') {
      // Find connected tool node - tool connects from task's 'tool' handle
      const connectedToolEdge = edges.find(e =>
          e.source === node.id &&
          e.sourceHandle === 'tool'
      );

      // If no tool is connected, clear methods
      if (!connectedToolEdge) {
        setNodes((nds) => {
          return nds.map((n) => {
            if (n.id === node.id) {
              return {
                ...n,
                data: {
                  ...n.data,
                  availableMethods: [],
                  methodDescriptions: {}
                }
              };
            }
            return n;
          });
        });

        setSelectedNodeWithProvider(node, {
          availableMethods: [],
          methodDescriptions: {}
        });
        setDrawerOpen(true);
        return;
      }

      if (connectedToolEdge) {
        const toolNode = nodes.find(n => n.id === connectedToolEdge.target);

        if (toolNode && toolNode.type === 'config' && toolNode.data.configType === 'tool') {
          // Get methods from tool node
          let methods = [];
          let methodDescriptions = {};

          // First, check if methods are directly in toolNode.data
          if (toolNode.data.methods && Array.isArray(toolNode.data.methods) && toolNode.data.methods.length > 0) {
            methods = toolNode.data.methods.map(m => m.name);
            toolNode.data.methods.forEach(m => {
              methodDescriptions[m.name] = {
                description: m.description || 'No description available',
                parameters: m.params ? m.params.map(p => ({
                  name: p.name,
                  type: p.type,
                  isOptional: p.isOptional || false,
                  default: p.default
                })) : [],
                returnType: m.returnType || 'Unknown',
                isAsync: m.isAsync || false
              };
            });
          } else {
            // Check if it's an MCP tool - look up by key in mcpToolNodes
            if (toolNode.data.key) {
              const mcpToolConfig = mcpToolNodes.find(t => t.key === toolNode.data.key);
              if (mcpToolConfig && mcpToolConfig.methods && Array.isArray(mcpToolConfig.methods)) {
                methods = mcpToolConfig.methods.map(m => m.name);
                mcpToolConfig.methods.forEach(m => {
                  methodDescriptions[m.name] = {
                    description: m.description || 'No description available',
                    parameters: m.params ? m.params.map(p => ({
                      name: p.name,
                      type: p.type,
                      isOptional: p.isOptional || false,
                      default: p.default
                    })) : [],
                    returnType: m.returnType || 'Unknown',
                    isAsync: m.isAsync || false
                  };
                });
              }
            }

            // If still no methods, check regular tools
            if (methods.length === 0) {
              const toolConfig = toolNodes.find(t => t.handlerName === toolNode.data.handlerName);
              if (toolConfig && toolConfig.methods && Array.isArray(toolConfig.methods)) {
                methods = toolConfig.methods.map(m => m.name);
                toolConfig.methods.forEach(m => {
                  methodDescriptions[m.name] = {
                    description: m.description || 'No description available',
                    parameters: m.params ? m.params.map(p => ({
                      name: p.name,
                      type: p.type,
                      isOptional: p.isOptional || false,
                      default: p.default
                    })) : [],
                    returnType: m.returnType || 'Unknown',
                    isAsync: m.isAsync || false
                  };
                });
              }
            }
          }

          if (methods.length > 0) {
            // Update nodes state
            setNodes((nds) => {
              return nds.map((n) => {
                if (n.id === node.id) {
                  return {
                    ...n,
                    data: {
                      ...n.data,
                      availableMethods: methods,
                      methodDescriptions: methodDescriptions
                    }
                  };
                }
                return n;
              });
            });

            // Set selected node with updated methods and open drawer
            setSelectedNodeWithProvider(node, {
              availableMethods: methods,
              methodDescriptions: methodDescriptions
            });
            setDrawerOpen(true);
            return; // Exit early since we've handled everything
          }
        }
      }

      // Set selected node and open drawer (if methods weren't found or no tool connected)
      setSelectedNodeWithProvider(node);
      setDrawerOpen(true);
    } else if (node.type === 'config' && node.data.configType === 'llm') {
      setSelectedNodeWithProvider(node);
      setDrawerOpen(true);
    } else if (node.type === 'config' && node.data.configType === 'tool') {
      // Explicitly handle tool nodes
      setSelectedNodeWithProvider(node);
      setDrawerOpen(true);
    } else {
      setSelectedNodeWithProvider(node);
      setDrawerOpen(true);
    }

    if (node.type === 'input') {
      setConfigModalData({
        nodeType: node.type,
        initialData: node.data
      });
      setShowNodeConfigModal(true);
      setSelectedNodeWithProvider(node); // for highlighting
      setDrawerOpen(true);
    } else {
      // For input nodes and other nodes, open sidebar
      setSelectedNode(node);
      setDrawerOpen(true);
    }
  }, [nodes, edges, toolNodes, mcpToolNodes]);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    // Deselect all edges when clicking on pane
    setEdges((eds) => eds.map(edge => ({ ...edge, selected: false })));
  }, []);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedNode(null);
  };

  // Fit and zoom out on init
  const handleInit = useCallback((instance) => {
    setRfInstance(instance);
    rfInstanceRef.current = instance;
    // Fit to screen by default ONLY when we have a connected workflow (nodes + edges).
    // Otherwise keep a normal viewport (prevents tiny "empty" workflows from zooming oddly).
    requestAnimationFrame(() => {
      try {
        const hasNodes = (nodes?.length ?? 0) > 0;
        const hasEdges = (edges?.length ?? 0) > 0;
        if (hasNodes && hasEdges) {
          const nodeCount = nodes?.length ?? 0;
          // Goal: keep initial view reasonably "small" (more zoomed-out),
          // especially when there are many nodes so `fitView` can include everything.
          const fitPadding = nodeCount > 20 ? 0.08 : nodeCount > 10 ? 0.09 : 0.11;
          const fitMinZoom = nodeCount > 20 ? 0.55 : nodeCount > 10 ? 0.65 : 0.7;
          // Prevent tiny graphs from zooming in too much.
          const fitMaxZoom = nodeCount > 10 ? 1.5 : 1.1;
          instance.fitView({
            padding: fitPadding,
            includeHiddenNodes: false,
            duration: 300,
            minZoom: fitMinZoom,
            maxZoom: fitMaxZoom,
          });
        } else {
          instance.setViewport({ x: 0, y: 0, zoom: 1.0 });
        }
      } catch {
        // ignore
      }
    });
  }, [nodes?.length, edges?.length]);

  // Also fit-to-screen when nodes first load (prevents default zoom when workflow is fetched)
  const didInitialFitRef = useRef(false);
  useEffect(() => {
    if (didInitialFitRef.current) return;
    if (!nodes?.length || !edges?.length) return;
    const instance = rfInstanceRef.current || rfInstance;
    if (!instance) return;
    didInitialFitRef.current = true;
    requestAnimationFrame(() => {
      try {
        const nodeCount = nodes?.length ?? 0;
        const fitPadding = nodeCount > 20 ? 0.08 : nodeCount > 10 ? 0.09 : 0.11;
        const fitMinZoom = nodeCount > 20 ? 0.55 : nodeCount > 10 ? 0.65 : 0.7;
        const fitMaxZoom = nodeCount > 10 ? 1.5 : 1.1;
        instance.fitView({
          padding: fitPadding,
          includeHiddenNodes: false,
          duration: 300,
          minZoom: fitMinZoom,
          maxZoom: fitMaxZoom,
        });
      } catch {
        // ignore
      }
    });
  }, [nodes?.length, edges?.length, rfInstance]);

  // Add tool node from modal event
  useEffect(() => {
    function handleAddToolToWorkflow(e) {
      let { handlerName, agentNodeId, toolType, toolData } = e.detail;

      // Use toolData from event if available, otherwise fallback to toolNodes lookup
      let toolConfig = toolData;

      if (!toolConfig) {
        // Fallback to toolNodes lookup if toolData not provided
        if (toolType === 'tool') {
          toolConfig = toolNodes.find(t => t.handlerName === handlerName);
        } else if (toolType === 'mcp') {
          toolConfig = mcpToolNodes.find(t => t.key === handlerName);
          handlerName = toolConfig?.handlerName;
        }
      } else {
        // If toolData provided, use the handlerName from it
        if (toolType === 'mcp') {
          handlerName = toolConfig.handlerName;
        }
      }
      // Unique id
      const newId = `config-tool-${handlerName}-${+new Date()}`;

      if (agentNodeId) {
        // Get current nodes state
        setNodes(currentNodes => {
          // If agentNodeId is provided, create tool node below the agent and connect them
          const agentNode = currentNodes.find(n => n.id === agentNodeId);
          if (!agentNode) return currentNodes;
          // Position tool node below the agent
          const position = {
            x: agentNode.position.x + 60,
            y: agentNode.position.y + 200
          };

          // Create tool node
          const toolNode = {
            id: newId,
            type: 'config',
            position,
            data: {
              configType: 'tool',
              key: toolConfig?.key,
              handlerName,
              displayName: toolConfig?.displayName || handlerName,
              srcPath: toolConfig?.srcPath || null,
              icon: toolConfig?.icon,
              iconColor: toolConfig?.iconColor,
              color: toolConfig?.color,
              attributes: toolConfig?.attributes,
              dependencies: toolConfig?.dependencies || null,
              provider: toolConfig?.provider || null,
              methods: toolConfig?.methods || null, // Store methods if available
            },
          };

          // Create edge from agent to tool
          const edgeId = `${agentNodeId}-${newId}`;
          const newEdge = {
            id: edgeId,
            source: agentNodeId,
            target: newId,
            sourceHandle: 'tool', // Connect to the tool handle
            type: 'dashed',
            data: {
              sourceType: 'agent',
              targetType: 'config'
            }
          };

          // Add edge outside of setNodes
          setEdges(eds => eds.concat(newEdge));

          // Set selected node to open config sidebar
          setSelectedNode(toolNode);
          setDrawerOpen(true);

          // Return updated nodes with tool added and agent updated
          return currentNodes.map(node => {
            if (node.id === agentNodeId) {
              return {
                ...node,
                data: {
                  ...node.data,
                  tools: [...(node.data.tools || []), handlerName]
                }
              };
            }
            return node;
          }).concat(toolNode);
        });
        return;
      } else {
        // Default behavior: center of viewport
        let position = { x: 400, y: 200 };
        if (rfInstance && reactFlowWrapper.current) {
          const bounds = reactFlowWrapper.current.getBoundingClientRect();
          position = rfInstance.project({
            x: bounds.width / 2,
            y: bounds.height / 2,
          });
        }

        // Add config node
        const toolNode = {
          id: newId,
          type: 'config',
          position,
          data: {
            configType: 'tool',
            key: toolConfig?.key,
            handlerName,
            displayName: toolConfig?.displayName || handlerName,
            srcPath: toolConfig?.srcPath || null,
            provider: toolConfig?.provider || null,
            icon: toolConfig?.icon,
            iconColor: toolConfig?.iconColor,
            color: toolConfig?.color,
            attributes: toolConfig?.attributes,
            dependencies: toolConfig?.dependencies || null,
            methods: toolConfig?.methods || null, // Store methods if available
          },
        };

        setNodes(nds => nds.concat(toolNode));

        // Set selected node to open config sidebar
        setSelectedNode(toolNode);
        setDrawerOpen(true);
      }
    }
    window.addEventListener('add-tool-to-workflow', handleAddToolToWorkflow);
    return () => window.removeEventListener('add-tool-to-workflow', handleAddToolToWorkflow);
  }, [rfInstance]); // Removed nodes dependency to prevent memory leaks

  // Add LLM node from modal event
  useEffect(() => {
    function handleAddLLMToWorkflow(e) {
      const { llmType, model, agentNodeId, llmData, baseUrl } = e.detail;
      // Use llmData from event if available, otherwise fallback to llmNodes lookup
      const llmConfig = llmData || llmNodes.find(l => l.llmType === llmType);
      const resolvedLlmType = normalizeLlmType(llmType);
      const isCustomLlm = isCustomLlmType(resolvedLlmType);
      
      // Unique id
      const newId = `config-llm-${resolvedLlmType}-${+new Date()}`;

      const buildCustomOrStandardLlmData = () => ({
        configType: 'llm',
        llmType: resolvedLlmType,
        displayName: llmConfig?.displayName || resolvedLlmType,
        icon: llmConfig?.icon,
        iconColor: llmConfig?.iconColor,
        color: llmConfig?.color,
        model: model || '',
        baseUrl: baseUrl || '',
        ...(isCustomLlm ? {} : {
          apiKey: '',
          apiVersion: '',
          asyncMode: false,
          embedModel: '',
        }),
      });

      if (agentNodeId) {
        // Get current nodes state
        setNodes(currentNodes => {
          // If agentNodeId is provided, create LLM node below the agent and connect them
          const agentNode = currentNodes.find(n => n.id === agentNodeId);
          if (!agentNode) return currentNodes;

          // Position LLM node below the agent (on the left side)
          const position = {
            x: agentNode.position.x - 60,
            y: agentNode.position.y + 200
          };

          // Create LLM node
          const llmNode = {
            id: newId,
            type: 'config',
            position,
            data: buildCustomOrStandardLlmData(),
          };

          // Create edge from agent to LLM
          const edgeId = `${agentNodeId}-${newId}`;
          const newEdge = {
            id: edgeId,
            source: agentNodeId,
            target: newId,
            sourceHandle: 'chatModel', // Connect to the chatModel handle
            type: 'dashed',
            data: {
              sourceType: 'agent',
              targetType: 'config'
            }
          };

          // Add edge outside of setNodes
          setEdges(eds => eds.concat(newEdge));

          // Return updated nodes with LLM added and agent updated
          return currentNodes.map(node => {
            if (node.id === agentNodeId) {
              return {
                ...node,
                data: {
                  ...node.data,
                  llm: resolvedLlmType
                }
              };
            }
            return node;
          }).concat(llmNode);
        });
        return;
      } else {
        // Default behavior: center of viewport
        let position = { x: 400, y: 200 };
        if (rfInstance && reactFlowWrapper.current) {
          const bounds = reactFlowWrapper.current.getBoundingClientRect();
          position = rfInstance.project({
            x: bounds.width / 2,
            y: bounds.height / 2,
          });
        }

        // Add config node
        setNodes(nds => nds.concat({
          id: newId,
          type: 'config',
          position,
          data: buildCustomOrStandardLlmData(),
        }));
      }
    }
    window.addEventListener('add-llm-to-workflow', handleAddLLMToWorkflow);
    return () => window.removeEventListener('add-llm-to-workflow', handleAddLLMToWorkflow);
  }, [rfInstance]); // Removed nodes dependency to prevent memory leaks

  // Add node from click event (input, agent, output, llm)
  useEffect(() => {
    function handleAddNodeToWorkflow(e) {
      const { nodeType, nodeData } = e.detail;
      let position = { x: 50, y: 150 };
      // Prevent multiple input nodes - use nodesRef to avoid stale closure
      const currentNodes = nodesRef.current;
      if (nodeType === 'input' && currentNodes.some(n => n.type === 'input')) {
        showNotificationGlobal('Only one input node is allowed', 'warning', 'bottomRight');
        return;
      }
      // Unique id
      const newId = `${nodeType}-${+new Date()}`;
      let data = nodeData;
      if (nodeType === 'agent') {
        data = {
          ...nodeData,
          tools: [],
          llm: '',
          memory: false,
          onConfigNodeAdd: (configType, value) => handleConfigNodeAdd(newId, configType, value),
          onChange: (data) => handleNodeConfigChange(data),
          selected: false,
        };
      }
      if (nodeType === 'llm') {
        data = {
          ...nodeData,
          // Add any default llm data if needed
        };
      }
      const newNode = {
        id: newId,
        type: nodeType === 'llm' ? 'config' : nodeType,
        position,
        data: nodeType === 'llm' ? { ...data, configType: 'llm' } : data,
      };
      setNodes(nds => nds.concat(newNode));
      // Open config drawer for agent or llm
      if (nodeType === 'agent' || nodeType === 'llm') {
        setSelectedNode(newNode);
        setDrawerOpen(true);
      }
    }
    window.addEventListener('add-node-to-workflow', handleAddNodeToWorkflow);
    return () => window.removeEventListener('add-node-to-workflow', handleAddNodeToWorkflow);
  }, [rfInstance]); // Removed nodes dependency to prevent memory leaks

  // Cascade-delete an agent/task node and its connected LLM + tool config nodes
  const performAgentOrTaskDelete = useCallback((nodeId) => {
    const currentEdges = edgesRef.current || [];
    const configNodeIds = [
      ...new Set(
          currentEdges
              .filter(edge =>
                  edge.source === nodeId &&
                  (edge.sourceHandle === 'chatModel' || edge.sourceHandle === 'tool')
              )
              .map(edge => edge.target)
      )
    ];
    const idsToRemove = new Set([nodeId, ...configNodeIds]);

    setEdges((eds) =>
        eds.filter(edge => !idsToRemove.has(edge.source) && !idsToRemove.has(edge.target))
    );

    setNodes((nds) =>
        nds.filter(node => {
          if (node.id === nodeId) return false;
          if (
              configNodeIds.includes(node.id) &&
              node.type === 'config' &&
              (node.data.configType === 'llm' || node.data.configType === 'tool')
          ) {
            return false;
          }
          return true;
        })
    );

    setSelectedNode((prev) => {
      if (!prev) return null;
      if (prev.id === nodeId || configNodeIds.includes(prev.id)) {
        setDrawerOpen(false);
        return null;
      }
      return prev;
    });
  }, []);

  // Handle delete node events
  useEffect(() => {
    function handleDeleteNode(e) {
      setDrawerOpen(false);
      const { nodeId, nodeType } = e.detail;

      // Agent and task agent: ask confirmation, then cascade-delete connected LLM/tool nodes
      if (nodeType === 'agent' || nodeType === 'task') {
        setPendingDeleteNode({ nodeId, nodeType });
        setShowDeleteNodeConfirm(true);
        return;
      }

      // Config and other nodes: delete immediately
      setNodes((nds) => nds.filter(node => node.id !== nodeId));
      setEdges((eds) => eds.filter(edge => edge.source !== nodeId && edge.target !== nodeId));

      if (selectedNode && selectedNode.id === nodeId) {
        setSelectedNode(null);
        setDrawerOpen(false);
      }
    }

    window.addEventListener('delete-node', handleDeleteNode);
    return () => window.removeEventListener('delete-node', handleDeleteNode);
  }, [selectedNode]);

  // Handle clear workflow event - keep only input node
  useEffect(() => {
    function handleClearWorkflow() {
      // Clearing resets state and should be considered clean until user edits again.
      markWorkflowClean();
      // Keep only input nodes, remove all other nodes (agents, config, output)
      setNodes((nds) => {
        const inputNodes = nds.filter(node => node.type === 'input');
        // If no input node exists, create a default one at a reasonable position
        let finalNodes;
        if (inputNodes.length === 0) {
          finalNodes = [{
            id: `input-${Date.now()}`,
            type: 'input',
            position: { x: 100, y: 100 },
            data: {
              label: 'Input',
              inputType: 'text'
            }
          }];
        } else {
          // Return only the first input node (in case there are multiple)
          // Reset its hasConnection state
          finalNodes = inputNodes.slice(0, 1).map(node => ({
            ...node,
            data: {
              ...node.data,
              hasConnection: false
            }
          }));
        }
        // Update saved state to cleared state (input node only, no edges)
        // This ensures that if user adds nodes/edges after clearing, changes will be detected
        savedNodesRef.current = JSON.parse(JSON.stringify(finalNodes));
        savedEdgesRef.current = [];
        return finalNodes;
      });

      // Remove all edges
      setEdges([]);

      // Close drawer and clear selection
      setSelectedNode(null);
      setDrawerOpen(false);
      setConsoleDrawerOpen(false);
      setConsoleNodeData(null);

      // Clear invalid agent IDs and unconnected node IDs
      setInvalidAgentIds([]);
      setUnconnectedNodeIds([]);
    }

    window.addEventListener('clear-workflow', handleClearWorkflow);
    return () => window.removeEventListener('clear-workflow', handleClearWorkflow);
  }, []);

  // Remove highlight on node/edge change
  const handleNodesChange = (...args) => {
    setUnconnectedNodeIds([]);
    onNodesChange(...args);
  };

  const handleEdgesChange = (...args) => {
    setUnconnectedNodeIds([]);
    // Check if this is a manual deletion (not from ReactFlow's built-in deletion)
    const changes = args[0];
    const hasManualDeletion = changes.some(change =>
        change.type === 'remove' &&
        change.id &&
        edges.find(edge => edge.id === change.id)?.selected
    );

    if (hasManualDeletion) {
      // Allow manual deletions (from our delete button)
      setEdges((eds) => eds.filter(edge => !changes.some(change =>
          change.type === 'remove' && change.id === edge.id
      )));
    } else {
      // Use the original onEdgesChange logic for other changes
      onEdgesChange(...args);
    }
  };

  // Monitor nodes and dispatch event when agent count changes
  useEffect(() => {
    const hasAgents = nodes.some(n => n.type === 'agent');
    const hasTasks = nodes.some(n => n.type === 'task');

    const event = new CustomEvent('workflow-agents-changed', {
      detail: { hasAgents, hasTasks }
    });

    window.dispatchEvent(event);
  }, [nodes]);


  // Monitor nodes and dispatch event when input count changes
  useEffect(() => {
    const hasInputs = nodes.some(n => n.type === 'input');
    const event = new CustomEvent('workflow-inputs-changed', {
      detail: { hasInputs }
    });
    window.dispatchEvent(event);
  }, [nodes]);

  // Auto-clear invalid agent highlighting once the graph becomes valid.
  // `invalidAgentIds` is set during validation, but connections can be fixed afterwards without re-validating.
  useEffect(() => {
    if (!invalidAgentIds || invalidAgentIds.length === 0) return;

    const currentNodes = nodes || [];
    const currentEdges = edges || [];

    const agentNodes = currentNodes.filter((n) => n?.type === 'agent');
    const stillInvalid = agentNodes
        .filter((agent) => {
          if (!agent?.id) return false;
          // Agent is valid only if it has outgoing edges to both tool + llm config nodes
          const outgoing = currentEdges.filter((e) => e?.source === agent.id);
          let hasTool = false;
          let hasLlm = false;
          for (const e of outgoing) {
            const targetNode = currentNodes.find((n) => n?.id === e?.target);
            if (targetNode?.type === 'config' && targetNode?.data?.configType === 'tool') hasTool = true;
            if (targetNode?.type === 'config' && targetNode?.data?.configType === 'llm') hasLlm = true;
          }
          return !(hasTool && hasLlm);
        })
        .map((a) => a.id);

    // Only update state if it actually changed, to avoid rerender loops.
    const same =
        stillInvalid.length === invalidAgentIds.length &&
        stillInvalid.every((id) => invalidAgentIds.includes(id));

    if (!same) {
      setInvalidAgentIds(stillInvalid);
    }
  }, [nodes, edges, invalidAgentIds]);

  // Edge handlers for interactive buttons
  const handleEdgeDelete = useCallback((edgeId) => {
    // First select the edge
    const edgeToDelete = edges.find(edge => edge.id === edgeId);
    if (edgeToDelete) {
      // Update the edge to be selected
      setEdges((eds) => {
        return eds.map(edge =>
            edge.id === edgeId ? { ...edge, selected: true } : { ...edge, selected: false }
        );
      });

      // Small delay to show the selection, then delete
      const timeoutId = setTimeout(() => {
        // Directly remove the edge without going through onEdgesChange
        setEdges((eds) => {
          return eds.filter(edge => edge.id !== edgeId);
        });
      }, 200);

      // Cleanup timeout on unmount (stored in ref for cleanup)
      return () => clearTimeout(timeoutId);
    } else {
    }
  }, [edges]);

  // Handle delete edge events using event system
  useEffect(() => {
    function handleDeleteEdge(e) {
      const { edgeId } = e.detail;
      // Call the handleEdgeDelete function
      handleEdgeDelete(edgeId);
    }
    window.addEventListener('delete-edge', handleDeleteEdge);
    return () => {
      window.removeEventListener('delete-edge', handleDeleteEdge);
    };
  }, [handleEdgeDelete]);

  // Add agent from input node '+' button
  useEffect(() => {
    function handleAddAgentFromInput(e) {
      const { inputNodeId, position } = e.detail;

      // Get current nodes state
      setNodes(currentNodes => {
        // Find the input node in state
        const inputNode = currentNodes.find(n => n.id === inputNodeId);
        if (!inputNode) return currentNodes;

        // Place agent node to the right of input node
        const agentX = inputNode.position.x + 220;
        const agentY = inputNode.position.y;
        const agentId = `agent-${+new Date()}-${localStorage.getItem('Uid')}`;
        const agentNode = {
          id: agentId,
          type: 'agent',
          position: { x: agentX, y: agentY },
          data: {
            title: 'Agent',
            subtitle: 'description',
            role: '',
            goal: '',
            tools: [],
            llm: '',
            memory: false,
            maxRetry: 1,
            onConfigNodeAdd: (configType, value) => handleConfigNodeAdd(agentId, configType, value),
            onChange: (data) => handleNodeConfigChange(data),
            selected: false,
          },
        };

        // Add edge from input to agent
        const edgeId = `${inputNodeId}-${agentId}`;
        const newEdge = {
          id: edgeId,
          source: inputNodeId,
          target: agentId,
          type: 'line',
          // Input node's source handle id is "a" (see `InputNode.js`)
          sourceHandle: 'a',
          data: {
            sourceType: 'input',
            targetType: 'agent'
          }
        };

        // Add new nodes and edges
        setEdges(eds => eds.concat(newEdge));
        setSelectedNode(agentNode);

        // Return updated nodes array
        return [...currentNodes, agentNode];
      });
    }
    window.addEventListener('add-agent-from-input', handleAddAgentFromInput);
    return () => window.removeEventListener('add-agent-from-input', handleAddAgentFromInput);
  }, []); // Removed nodes dependency to prevent memory leaks

  // Add task from input node '+' button
  useEffect(() => {
    function handleAddTaskFromInput(e) {
      const { inputNodeId, position } = e.detail;

      // Get current nodes state
      setNodes(currentNodes => {
        // Find the input node in state
        const inputNode = currentNodes.find(n => n.id === inputNodeId);
        if (!inputNode) return currentNodes;

        // Place task node to the right of input node
        const taskX = inputNode.position.x + 220;
        const taskY = inputNode.position.y;
        const taskId = `task-${+new Date()}`;
        const taskNode = {
          id: taskId,
          type: 'task',
          position: { x: taskX, y: taskY },
          data: {
            title: 'Task Agent',
            subtitle: 'description',
            tools: [],
            maxRetry: 1,
            instructionGroups: [],
            onConfigNodeAdd: (configType, value) => handleConfigNodeAdd(taskId, configType, value),
            onChange: (data) => handleNodeConfigChange(data),
            selected: false,
          },
        };

        // Add edge from input to task
        const edgeId = `${inputNodeId}-${taskId}`;
        const newEdge = {
          id: edgeId,
          source: inputNodeId,
          target: taskId,
          type: 'line',
          // Input node's source handle id is "a" (see `InputNode.js`)
          sourceHandle: 'a',
          data: {
            sourceType: 'input',
            targetType: 'task'
          }
        };

        // Add new nodes and edges
        setEdges(eds => eds.concat(newEdge));
        setSelectedNode(taskNode);
        // Return updated nodes array
        return [...currentNodes, taskNode];
      });
    }
    window.addEventListener('add-task-from-input', handleAddTaskFromInput);
    return () => window.removeEventListener('add-task-from-input', handleAddTaskFromInput);
  }, []); // Removed nodes dependency to prevent memory leaks

  // Add task from agent node '+' button
  useEffect(() => {
    function handleAddTaskFromAgent(e) {
      const { agentNodeId, position } = e.detail;

      // Get current nodes state
      setNodes(currentNodes => {
        // Find the agent node in state
        const agentNode = currentNodes.find(n => n.id === agentNodeId);
        if (!agentNode) return currentNodes;

        // Place task node to the right of agent node
        const taskX = agentNode.position.x + 220;
        const taskY = agentNode.position.y;
        const taskId = `task-${+new Date()}`;
        const taskNode = {
          id: taskId,
          type: 'task',
          position: { x: taskX, y: taskY },
          data: {
            title: 'Task Agent',
            subtitle: 'description',
            role: '',
            goal: '',
            tools: [],
            llm: '',
            memory: false,
            maxRetry: 1,
            instructionGroups: [],
            onConfigNodeAdd: (configType, value) => handleConfigNodeAdd(taskId, configType, value),
            onChange: (data) => handleNodeConfigChange(data),
            selected: false,
          },
        };

        // Add edge from agent to task
        const edgeId = `${agentNodeId}-${taskId}`;
        const newEdge = {
          id: edgeId,
          source: agentNodeId,
          target: taskId,
          type: 'line',
          sourceHandle: 'agentOutput',
          data: {
            sourceType: 'agent',
            targetType: 'task'
          }
        };

        // Add new nodes and edges
        setEdges(eds => eds.concat(newEdge));
        setSelectedNode(taskNode);
        // Return updated nodes array
        return [...currentNodes, taskNode];
      });
    }
    window.addEventListener('add-task-from-agent', handleAddTaskFromAgent);
    return () => window.removeEventListener('add-task-from-agent', handleAddTaskFromAgent);
  }, []); // Removed nodes dependency to prevent memory leaks

  // Add agent from agent node '+' button
  useEffect(() => {
    function handleAddAgentFromAgent(e) {
      const { agentNodeId, position } = e.detail;

      setNodes((currentNodes) => {
        const agentNode = currentNodes.find((n) => n.id === agentNodeId);
        if (!agentNode) return currentNodes;

        const agentX = agentNode.position.x + 220;
        const agentY = agentNode.position.y;
        const newAgentId = `agent-${+new Date()}-${localStorage.getItem('Uid')}`;
        const newAgentNode = {
          id: newAgentId,
          type: 'agent',
          position: { x: agentX, y: agentY },
          data: {
            title: 'Agent',
            subtitle: 'description',
            role: '',
            goal: '',
            tools: [],
            llm: '',
            memory: false,
            maxRetry: 1,
            onConfigNodeAdd: (configType, value) => handleConfigNodeAdd(newAgentId, configType, value),
            onChange: (data) => handleNodeConfigChange(data),
            selected: false
          }
        };

        const edgeId = `${agentNodeId}-${newAgentId}`;
        const newEdge = {
          id: edgeId,
          source: agentNodeId,
          target: newAgentId,
          type: 'line',
          // Agent node's source handle id is "agentOutput" (see `AgentNode.js`)
          sourceHandle: 'agentOutput',
          data: {
            sourceType: 'agent',
            targetType: 'agent'
          }
        };

        setEdges((eds) => eds.concat(newEdge));
        setSelectedNode(newAgentNode);
        return [...currentNodes, newAgentNode];
      });
    }

    window.addEventListener('add-agent-from-agent', handleAddAgentFromAgent);
    return () => window.removeEventListener('add-agent-from-agent', handleAddAgentFromAgent);
  }, []); // Removed nodes dependency to prevent memory leaks


  // Add agent from task node '+' button
  useEffect(() => {
    function handleAddAgentFromTask(e) {
      const { taskNodeId, position } = e.detail;

      // Get current nodes state
      setNodes(currentNodes => {
        // Find the task node in state
        const taskNode = currentNodes.find(n => n.id === taskNodeId);
        if (!taskNode) return currentNodes;

        // Place agent node to the right of task node
        const agentX = taskNode.position.x + 220;
        const agentY = taskNode.position.y;
        const agentId = `agent-${+new Date()}-${localStorage.getItem('Uid')}`;
        const agentNode = {
          id: agentId,
          type: 'agent',
          position: { x: agentX, y: agentY },
          data: {
            title: 'Agent',
            subtitle: 'description',
            role: '',
            goal: '',
            tools: [],
            llm: '',
            memory: false,
            maxRetry: 1,
            onConfigNodeAdd: (configType, value) => handleConfigNodeAdd(agentId, configType, value),
            onChange: (data) => handleNodeConfigChange(data),
            selected: false,
          },
        };

        // Add edge from task to agent
        const edgeId = `${taskNodeId}-${agentId}`;
        const newEdge = {
          id: edgeId,
          source: taskNodeId,
          target: agentId,
          type: 'line',
          sourceHandle: 'taskOutput',
          data: {
            sourceType: 'task',
            targetType: 'agent'
          }
        };

        // Add new nodes and edges
        setEdges(eds => eds.concat(newEdge));
        setSelectedNode(agentNode);

        // Return updated nodes array
        return [...currentNodes, agentNode];
      });
    }
    window.addEventListener('add-agent-from-task', handleAddAgentFromTask);
    return () => window.removeEventListener('add-agent-from-task', handleAddAgentFromTask);
  }, []); // Removed nodes dependency to prevent memory leaks

  // Add task from task node '+' button
  useEffect(() => {
    function handleAddTaskFromTask(e) {
      const { taskNodeId, position } = e.detail;

      // Get current nodes state
      setNodes(currentNodes => {
        // Find the task node in state
        const taskNode = currentNodes.find(n => n.id === taskNodeId);
        if (!taskNode) return currentNodes;

        // Place task node to the right of task node
        const newTaskX = taskNode.position.x + 220;
        const newTaskY = taskNode.position.y;
        const newTaskId = `task-${+new Date()}`;
        const newTaskNode = {
          id: newTaskId,
          type: 'task',
          position: { x: newTaskX, y: newTaskY },
          data: {
            title: 'Task Agent',
            subtitle: 'description',
            role: '',
            goal: '',
            tools: [],
            llm: '',
            memory: false,
            maxRetry: 1,
            instructionGroups: [],
            onConfigNodeAdd: (configType, value) => handleConfigNodeAdd(newTaskId, configType, value),
            onChange: (data) => handleNodeConfigChange(data),
            selected: false,
          },
        };

        // Add edge from task to task
        const edgeId = `${taskNodeId}-${newTaskId}`;
        const newEdge = {
          id: edgeId,
          source: taskNodeId,
          target: newTaskId,
          type: 'line',
          sourceHandle: 'taskOutput',
          data: {
            sourceType: 'task',
            targetType: 'task'
          }
        };

        // Add new nodes and edges
        setEdges(eds => eds.concat(newEdge));
        setSelectedNode(newTaskNode);

        // Return updated nodes array
        return [...currentNodes, newTaskNode];
      });
    }
    window.addEventListener('add-task-from-task', handleAddTaskFromTask);
    return () => window.removeEventListener('add-task-from-task', handleAddTaskFromTask);
  }, []); // Removed nodes dependency to prevent memory leaks
  // Handle console drawer open events
  useEffect(() => {
    function handleOpenConsoleDrawer(e) {
      const { nodeId, nodeType, nodeData, focusInput } = e.detail || {};
      // Validate before allowing console drawer to open
      const currentRfInstance = rfInstanceRef.current || rfInstance;
      if (currentRfInstance) {
        const currentNodes = currentRfInstance.getNodes();
        const currentEdges = currentRfInstance.getEdges();
        validateWorkflowRef.current(
            currentNodes,
            currentEdges,
            (message, type) => showAlert(message, type)
        ).then((isValid) => {
          if (isValid !== true) return;
          setConsoleNodeData({ nodeId, nodeType, nodeData });
          setFocusConsoleInput(focusInput === true);
          setConsoleDrawerOpen(true);
        });
        return;
      }
      setConsoleNodeData({ nodeId, nodeType, nodeData });
      setFocusConsoleInput(focusInput === true);
      setConsoleDrawerOpen(true);
    }

    // Listen to internal event system
    eventSystem.current.addEventListener('open-console-drawer', handleOpenConsoleDrawer);

    // Listen to window events (for external triggers like ProjectView)
    function handleWindowOpenConsole(e) {
      const { nodeId, nodeType, nodeData, focusInput } = e.detail || {};
      // Validate before allowing console drawer to open
      const openConsole = () => {
        // If no node data provided, try to find the first agent node
        if (!nodeId && !nodeData) {
          const agentNode = nodes.find(n => n.type === 'agent');
          if (agentNode) {
            setConsoleNodeData({
              nodeId: agentNode.id,
              nodeType: 'agent',
              nodeData: agentNode.data
            });
          } else {
            // Open with default/empty data
            setConsoleNodeData({
              nodeId: null,
              nodeType: 'workflow',
              nodeData: { title: 'Workflow Console' }
            });
          }
        } else {
          setConsoleNodeData({ nodeId, nodeType, nodeData });
        }
        setFocusConsoleInput(focusInput === true);
        // Set expand flag to expand console drawer if it's minimized
        setExpandConsoleOnOpen(true);
        setConsoleDrawerOpen(true);
      };

      const currentRfInstance = rfInstanceRef.current || rfInstance;
      if (currentRfInstance) {
        const currentNodes = currentRfInstance.getNodes();
        const currentEdges = currentRfInstance.getEdges();
        validateWorkflowRef.current(
            currentNodes,
            currentEdges,
            (message, type) => showAlert(message, type)
        ).then((isValid) => {
          if (isValid !== true) return;
          openConsole();
        });
        return;
      }
      openConsole();
    }

    window.addEventListener('open-console-drawer', handleWindowOpenConsole);

    return () => {
      eventSystem.current.removeEventListener('open-console-drawer', handleOpenConsoleDrawer);
      window.removeEventListener('open-console-drawer', handleWindowOpenConsole);
    };
  }, [nodes]);

  useEffect(() => {
    workloadWatchIdRef.current = workloadWatchId;
  }, [workloadWatchId]);

  useEffect(() => {
    const rememberPodStatus = (event) => {
      const detail = event?.detail;
      if (!detail) return;
      if (detail.status || detail.messageEvent === false) {
        podStatusDetailRef.current = detail;
      }
    };
    const syncPodStatus = () => {
      const detail = podStatusDetailRef.current;
      if (!detail) return;
      window.dispatchEvent(new CustomEvent('workflow-pod-status-sync', { detail }));
    };
    window.addEventListener('workflow-pod-status', rememberPodStatus);
    window.addEventListener('sync-workflow-pod-status', syncPodStatus);
    return () => {
      window.removeEventListener('workflow-pod-status', rememberPodStatus);
      window.removeEventListener('sync-workflow-pod-status', syncPodStatus);
    };
  }, []);

  useEffect(() => {
    const isMessageEventNow = () => isMessageEventWorkflow(nodesRef.current);

    const publishPodStatus = (status, options = {}) => {
      const messageEvent = options.messageEvent ?? isMessageEventNow();
      const storageId = options.projectId || ProjectID;
      if (storageId) {
        try {
          sessionStorage.setItem(`sa-workflow-pod:${storageId}`, status);
        } catch (error) {
          /* ignore storage failures */
        }
      }
      window.dispatchEvent(
          new CustomEvent('workflow-pod-status', { detail: { status, messageEvent } })
      );
      window.dispatchEvent(new CustomEvent('workflow-waiting-status', { detail: { message: status } }));
    };

    const applyLiveStatus = (label, projectId) => {
      if (!label) return;
      if (label === 'Running' || label === 'Stopping') {
        setIsWorkflowRunning(true);
        if (projectId) setWorkloadWatchId(projectId);
      } else if (label === 'Stopped' || label === 'Failed') {
        podStopRequestedRef.current = false;
        setIsWorkflowRunning(false);
        setRunningAgentId(null);
        if (label === 'Stopped') {
          window.dispatchEvent(new CustomEvent('workflow-stop'));
          window.dispatchEvent(new CustomEvent('agent-stop-all'));
          window.dispatchEvent(new CustomEvent('node-highlight-stop-all'));
        }
        if (label !== 'Stopping') {
          setWorkloadWatchId(null);
        }
      }
      publishPodStatus(label, { messageEvent: true, projectId });
    };

    const fetchLiveWorkloadStatus = async (projectId, { silent = false } = {}) => {
      if (!projectId) return null;
      try {
        const res = await ClusterService.getPod(projectId);
        const failed =
            res?.status === API_RESPONSE.FAILED
            || res?.status === API_RESPONSE.FAILURE
            || Boolean(res?.errorCode);
        const raw = extractLiveWorkloadRaw(res);
        if (failed && !raw) {
          if (!silent) {
            showNotificationGlobal(res?.errorMessage || 'Failed to load live status', 'error');
          }
          return null;
        }
        const phase = extractWorkloadPhase(raw);
        const label = mapWorkloadStatusLabel(phase);
        if (label) applyLiveStatus(label, projectId);
        return label;
      } catch (error) {
        if (!silent) {
          showNotificationGlobal('Failed to load live status', 'error');
        }
        return null;
      }
    };

    const reportPodStopResult = (detail) => {
      window.dispatchEvent(new CustomEvent('workflow-pod-stop-result', { detail }));
    };

    const handleStopPod = async () => {
      // Pod stop runs only for a Message Event (Kafka, RabbitMQ, MQTT, and the same type).
      if (!isMessageEventNow()) {
        reportPodStopResult({ success: false, status: null });
        return;
      }
      // Stop and Stop and Change share this check. Docker resets the running
      // state locally. Kubernetes calls the existing pod delete endpoint.
      if (localStorage.getItem('environmentType') === 'DOCKER') {
        dockerStopResetRef.current = true;
        podStopRequestedRef.current = false;
        setIsWorkflowRunning(false);
        setRunningAgentId(null);
        setWorkloadWatchId(null);
        if (ProjectID) {
          try {
            sessionStorage.removeItem(`sa-workflow-pod:${ProjectID}`);
          } catch (error) {
            /* ignore storage failures */
          }
        }
        window.dispatchEvent(new CustomEvent('workflow-pod-status', {
          detail: { status: null, messageEvent: false },
        }));
        window.dispatchEvent(new CustomEvent('workflow-waiting-status', { detail: { message: null } }));
        window.dispatchEvent(new CustomEvent('workflow-stop'));
        window.dispatchEvent(new CustomEvent('agent-stop-all'));
        window.dispatchEvent(new CustomEvent('node-highlight-stop-all'));
        reportPodStopResult({ success: true, status: 'Stopped' });
        return;
      }
      if (podStopInFlightRef.current) return;
      if (!ProjectID) {
        reportPodStopResult({ success: false, status: null });
        return;
      }
      podStopInFlightRef.current = true;
      podStopRequestedRef.current = true;
      publishPodStatus('Stopping', { messageEvent: true });
      try {
        const res = await ClusterService.deletePod(ProjectID);
        const failed =
            res?.status === API_RESPONSE.FAILED
            || res?.status === API_RESPONSE.FAILURE
            || Boolean(res?.errorCode || res?.errorMessage);
        if (failed) {
          podStopRequestedRef.current = false;
          showNotificationGlobal(res?.errorMessage || 'Failed to stop workload', 'error', 'bottomRight');
          await fetchLiveWorkloadStatus(ProjectID, { silent: true });
          reportPodStopResult({ success: false, status: 'Running' });
          return;
        }
        showNotificationGlobal('Workload stop started', 'success', 'bottomRight');
        setWorkloadWatchId(ProjectID);
        const label = await fetchLiveWorkloadStatus(ProjectID, { silent: true });
        reportPodStopResult({
          success: label === 'Stopped',
          pending: label !== 'Stopped' && label !== 'Failed',
          status: label || null,
        });
      } catch (error) {
        podStopRequestedRef.current = false;
        showNotificationGlobal('Failed to stop workload', 'error', 'bottomRight');
        await fetchLiveWorkloadStatus(ProjectID, { silent: true });
        reportPodStopResult({ success: false, status: null });
      } finally {
        podStopInFlightRef.current = false;
      }
    };

    const handlePodMessage = (event) => {
      let message = event?.detail;
      if (typeof message === 'string') {
        try {
          message = JSON.parse(message);
        } catch (error) {
          return;
        }
      }
      const info = message?.ninfo || message?.nInfo || message?.NInfo || {};
      const nested = info.msg && typeof info.msg === 'object' ? info.msg : {};
      const moduleType = info.moduleType || nested.moduleType || message?.moduleType;
      if (moduleType && moduleType !== 'POD') return;

      const messageEvent = isMessageEventNow();
      const watchId = workloadWatchIdRef.current || ProjectID;

      if (messageEvent && watchId) {
        const payload = info.dto || info.msg || info.data || info.payload || message?.dto || message?.data || nested;
        const candidates = Array.isArray(payload)
            ? payload
            : (payload && typeof payload === 'object' ? [payload] : []);
        const matched = candidates.find((raw) => workloadMatchesProject(raw, watchId));
        const phaseFromMatch = matched ? extractWorkloadPhase(matched) : '';
        const phaseFromInfo =
            info.status || info.podStatus || info.vmStatus || nested.status || nested.podStatus || '';
        const label = mapWorkloadStatusLabel(phaseFromMatch || phaseFromInfo);
        if (label) {
          applyLiveStatus(label, watchId);
          return;
        }
      }

      // Pod-status updates after Stop apply only while a Message Event stop is in progress.
      if (!messageEvent || !podStopRequestedRef.current) return;
      const text = String(
          info.status || info.podStatus || info.vmStatus || nested.status || nested.podStatus || ''
      ).toUpperCase().replace(/[\s_-]/g, '');
      if (!text) return;
      const stopping = text.includes('STOPPING') || text.includes('DELETING') || text === 'PROCESSING';
      const stopped = !stopping && (
          text.includes('STOPPED')
          || text === 'STOP'
          || text.endsWith('STOP')
          || text.includes('DELETED')
          || text.includes('COMPLETED')
          || text.includes('FINISHED')
          || text === 'SUCCESS'
          || text.includes('PODSUCCESS')
      );
      if (stopped) {
        podStopRequestedRef.current = false;
        setIsWorkflowRunning(false);
        setRunningAgentId(null);
        publishPodStatus('Stopped', { messageEvent: true });
        window.dispatchEvent(new CustomEvent('workflow-stop'));
        window.dispatchEvent(new CustomEvent('agent-stop-all'));
        window.dispatchEvent(new CustomEvent('node-highlight-stop-all'));
      } else if (stopping) {
        publishPodStatus('Stopping', { messageEvent: true });
      }
    };

    let savedStatus = '';
    if (ProjectID) {
      try {
        savedStatus = sessionStorage.getItem(`sa-workflow-pod:${ProjectID}`) || '';
      } catch (error) {
        savedStatus = '';
      }
    }
    const restoreTimer = window.setTimeout(() => {
      const messageEvent = isMessageEventNow();
      if (savedStatus === 'Running') {
        setIsWorkflowRunning(true);
        publishPodStatus('Running', { messageEvent });
        window.dispatchEvent(new CustomEvent('workflow-run', { detail: {} }));
        if (messageEvent) setWorkloadWatchId(ProjectID);
      } else if (savedStatus === 'Stopping') {
        podStopRequestedRef.current = true;
        setIsWorkflowRunning(true);
        publishPodStatus('Stopping', { messageEvent });
        window.dispatchEvent(new CustomEvent('workflow-run', { detail: {} }));
        if (messageEvent) setWorkloadWatchId(ProjectID);
      } else if (savedStatus === 'Stopped') {
        publishPodStatus('Stopped', { messageEvent });
      } else if (savedStatus === 'Failed') {
        publishPodStatus('Failed', { messageEvent: true });
      }
      // MESSAGE_EVENT: always reconcile with api/pod/get/{projectId}
      if (messageEvent && ProjectID) {
        fetchLiveWorkloadStatus(ProjectID, { silent: true }).then((label) => {
          if (label === 'Running' || label === 'Stopping') {
            window.dispatchEvent(new CustomEvent('workflow-run', { detail: {} }));
          }
        });
      }
    }, 0);

    window.addEventListener('stop-workflow-pod', handleStopPod);
    window.addEventListener('podMessageReceived', handlePodMessage);
    window.addEventListener('messageReceived', handlePodMessage);
    return () => {
      window.clearTimeout(restoreTimer);
      window.removeEventListener('stop-workflow-pod', handleStopPod);
      window.removeEventListener('podMessageReceived', handlePodMessage);
      window.removeEventListener('messageReceived', handlePodMessage);
    };
  }, [ProjectID]);

  // When nodes finish loading as MESSAGE_EVENT, sync workload status from api/pod/get/{projectId}
  useEffect(() => {
    if (!ProjectID) return undefined;
    if (!isMessageEventWorkflow(nodes)) return undefined;
    if (workloadWatchIdRef.current === ProjectID) {
      workloadProbedProjectRef.current = ProjectID;
      return undefined;
    }
    if (workloadProbedProjectRef.current === ProjectID) return undefined;
    workloadProbedProjectRef.current = ProjectID;
    let cancelled = false;
    (async () => {
      try {
        const res = await ClusterService.getPod(ProjectID);
        if (cancelled) return;
        const failed =
            res?.status === API_RESPONSE.FAILED
            || res?.status === API_RESPONSE.FAILURE
            || Boolean(res?.errorCode);
        const raw = extractLiveWorkloadRaw(res);
        if (failed && !raw) return;
        const label = mapWorkloadStatusLabel(extractWorkloadPhase(raw));
        if (label !== 'Running' && label !== 'Stopping') return;
        setIsWorkflowRunning(true);
        setWorkloadWatchId(ProjectID);
        try {
          sessionStorage.setItem(`sa-workflow-pod:${ProjectID}`, label);
        } catch (error) {
          /* ignore */
        }
        window.dispatchEvent(
            new CustomEvent('workflow-pod-status', {
              detail: { status: label, messageEvent: true },
            })
        );
        window.dispatchEvent(
            new CustomEvent('workflow-waiting-status', { detail: { message: label } })
        );
        window.dispatchEvent(new CustomEvent('workflow-run', { detail: {} }));
      } catch (error) {
        /* ignore probe failures on load */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ProjectID, nodes]);

  // MESSAGE_EVENT only: keep workload status in sync via live poll
  useEffect(() => {
    if (!workloadWatchId) return undefined;
    if (!isMessageEventWorkflow(nodesRef.current)) return undefined;
    dockerStopResetRef.current = false;

    let cancelled = false;
    const tick = async (silent) => {
      if (cancelled || dockerStopResetRef.current) return;
      try {
        const res = await ClusterService.getPod(workloadWatchId);
        if (cancelled || dockerStopResetRef.current) return;
        const failed =
            res?.status === API_RESPONSE.FAILED
            || res?.status === API_RESPONSE.FAILURE
            || Boolean(res?.errorCode);
        const raw = extractLiveWorkloadRaw(res);
        if (failed && !raw) {
          if (!silent) {
            showNotificationGlobal(res?.errorMessage || 'Failed to load live status', 'error');
          }
          return;
        }
        const label = mapWorkloadStatusLabel(extractWorkloadPhase(raw));
        if (!label) return;
        if (label === 'Running' || label === 'Stopping') {
          setIsWorkflowRunning(true);
        } else if (label === 'Stopped' || label === 'Failed') {
          podStopRequestedRef.current = false;
          setIsWorkflowRunning(false);
          setRunningAgentId(null);
          if (label === 'Stopped') {
            window.dispatchEvent(new CustomEvent('workflow-stop'));
            window.dispatchEvent(new CustomEvent('agent-stop-all'));
            window.dispatchEvent(new CustomEvent('node-highlight-stop-all'));
          }
          setWorkloadWatchId(null);
        }
        try {
          sessionStorage.setItem(`sa-workflow-pod:${workloadWatchId}`, label);
        } catch (error) {
          /* ignore */
        }
        window.dispatchEvent(
            new CustomEvent('workflow-pod-status', {
              detail: { status: label, messageEvent: true },
            })
        );
        window.dispatchEvent(
            new CustomEvent('workflow-waiting-status', { detail: { message: label } })
        );
      } catch (error) {
        if (!silent) {
          showNotificationGlobal('Failed to load live status', 'error');
        }
      }
    };

    tick(false);
    const timer = window.setInterval(() => tick(true), WORKLOAD_LIVE_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [workloadWatchId]);

  // Allow other pages/overlays to force-close the console drawer (e.g. Traces/ExecutionHistory views).
  useEffect(() => {
    const handleForceClose = () => {
      setConsoleDrawerOpen(false);
      setFocusConsoleInput(false);
      setExpandConsoleOnOpen(false);
    };
    window.addEventListener('force-close-console-drawer', handleForceClose);
    return () => window.removeEventListener('force-close-console-drawer', handleForceClose);
  }, []);

  // Listen for console drawer messages to use as query
  useEffect(() => {
    function handleWorkflowMessageSent(e) {
      const message = e.detail;
      if (message && typeof message === 'string' && message.trim()) {
        const trimmedMessage = message.trim();
        consoleQueryRef.current = trimmedMessage; // Update ref immediately
      }
    }

    window.addEventListener('workflow-message-sent', handleWorkflowMessageSent);

    return () => {
      window.removeEventListener('workflow-message-sent', handleWorkflowMessageSent);
    };
  }, []);

  // Handle agent run/stop events
  useEffect(() => {
    function handleAgentRun(e) {
      const { nodeId } = e.detail;
      setRunningAgentId(nodeId);
      setIsWorkflowRunning(true);
      // Dispatch event for ProjectView to track
      window.dispatchEvent(new CustomEvent('workflow-run', { detail: { nodeId } }));
    }

    function handleAgentStop(e) {
      setRunningAgentId(null);
      setIsWorkflowRunning(false);
      // Clear uid mapping when workflow stops
      agentUidToNodeIdMap.current.clear();
      // Clear window mapping as well
      if (window.agentUidToNodeIdMap) {
        window.agentUidToNodeIdMap.clear();
      }
      // Remove all highlighting when workflow stops
      window.dispatchEvent(new CustomEvent('agent-stop-all'));
      // Dispatch event for ProjectView to track
      window.dispatchEvent(new CustomEvent('workflow-stop'));
    }

    async function handleExecuteWorkflow(e) {
      if (isWorkflowRunningRef.current) {
        return;
      } else {
        // Get current nodes and edges from ReactFlow instance for validation
        const currentRfInstance = rfInstanceRef.current || rfInstance;
        if (!currentRfInstance) {
          showNotificationGlobal('Workflow component not ready. Please try again.');
          return;
        }

        const currentNodes = currentRfInstance.getNodes();
        const currentEdges = currentRfInstance.getEdges();

        // Get query from event detail if provided, otherwise use consoleQuery state
        const queryFromEvent = e?.detail?.query;
        let query = queryFromEvent !== undefined ? queryFromEvent : (consoleQueryRef.current || '');

        // Conversation sends the typed query. Manual Start, Message Event,
        // and every other event execute without a chat message.
        if (!query || !query.trim()) {
          query = 'Start workflow';
        }

        // Update query state if provided from event
        if (queryFromEvent) {
          consoleQueryRef.current = queryFromEvent; // Update ref immediately
        }

        // Comprehensive validation: structure + data content (forces required fields to be filled)
        const isValid = await validateWorkflowRef.current(currentNodes, currentEdges, (message, type) => showAlert(message, type));
        if (isValid !== true) {
          return;
        }
        // Build payload
        let payload;
        try {
          payload = buildWorkflowPayload(currentNodes, currentEdges, query);
        } catch (error) {
          showNotificationGlobal('Error building workflow payload: ' + error.message, 'error');
          return;
        }

        applyEvaluationToPayload(payload, evaluationSettingsRef.current);

        // Save workflow BEFORE execute
        let saveRes;
        try {
          saveRes = await WorkflowService.createWorkflow(payload);
        } catch (_) {
          showNotificationGlobal('Error saving workflow', 'error');
          return;
        }

        if (saveRes?.status !== API_RESPONSE.SUCCESS) {
          const errorMsg = saveRes?.message || 'Unknown error occurred';
          showNotificationGlobal('Failed to save workflow: ' + errorMsg, 'error');
          return;
        }

        // Update projectId (important for console/websocket filtering)
        const newProjectId = updateProjectIdFromResponse(saveRes, false);
        if (newProjectId) {
          payload.projectId = newProjectId;
          if (payload.appConfig) payload.appConfig.appAuthToken = newProjectId;
        }
        savedAppConfigRef.current = payload.appConfig;
        window.dispatchEvent(new CustomEvent('workflow-saved'));

        applyEvaluationToPayload(payload, evaluationSettingsRef.current);

        // Execute workflow via API
        let executeRes;
        try {
          executeRes = await WorkflowService.executeWorkflow(payload);
        } catch (err) {
          console.error('Error executing workflow:', err);
          const data = err?.response?.data;
          const userMessage = executeErrorMessage(
              data,
              err?.message || 'An error occurred while executing the workflow'
          );
          showAlert(userMessage, 'error');
          showNotificationGlobal(userMessage, 'error', 'bottomRight');

          const messageEvent = isMessageEventWorkflow(currentNodes);
          const launchedProjectId = payload.projectId || ProjectID;
          if (messageEvent && isEventAlreadyRunningError(data) && launchedProjectId) {
            setIsWorkflowRunning(true);
            try {
              sessionStorage.setItem(`sa-workflow-pod:${launchedProjectId}`, 'Running');
            } catch (error) {
              /* ignore storage failures */
            }
            window.dispatchEvent(
                new CustomEvent('workflow-pod-status', {
                  detail: { status: 'Running', messageEvent: true },
                })
            );
            window.dispatchEvent(
                new CustomEvent('workflow-waiting-status', { detail: { message: 'Running' } })
            );
            setWorkloadWatchId(launchedProjectId);
            const agentNode = currentNodes.find((n) => n.type === 'agent');
            window.dispatchEvent(new CustomEvent('workflow-run', { detail: { nodeId: agentNode?.id } }));
          }
          return;
        }

        if (executeRes?.status !== API_RESPONSE.SUCCESS) {
          const errorMsg = executeErrorMessage(executeRes);
          showAlert(errorMsg, 'error');
          showNotificationGlobal(errorMsg, 'error', 'bottomRight');

          // Backend already has the MESSAGE_EVENT workload running — switch UI to Stop
          const messageEvent = isMessageEventWorkflow(currentNodes);
          const launchedProjectId = payload.projectId || ProjectID;
          if (messageEvent && isEventAlreadyRunningError(executeRes) && launchedProjectId) {
            setIsWorkflowRunning(true);
            try {
              sessionStorage.setItem(`sa-workflow-pod:${launchedProjectId}`, 'Running');
            } catch (error) {
              /* ignore storage failures */
            }
            window.dispatchEvent(
                new CustomEvent('workflow-pod-status', {
                  detail: { status: 'Running', messageEvent: true },
                })
            );
            window.dispatchEvent(
                new CustomEvent('workflow-waiting-status', { detail: { message: 'Running' } })
            );
            setWorkloadWatchId(launchedProjectId);
            const agentNode = currentNodes.find((n) => n.type === 'agent');
            window.dispatchEvent(new CustomEvent('workflow-run', { detail: { nodeId: agentNode?.id } }));
          }
          return;
        }

        // Extract projectId from execute response and update if needed
        updateProjectIdFromResponse(executeRes, true);

        showNotificationGlobal('Workflow execution started', 'success', 'bottomRight');
        setIsWorkflowRunning(true);
        const launchedProjectId = payload.projectId || ProjectID;
        const messageEvent = isMessageEventWorkflow(currentNodes);
        // Pod Running status is only for Message Events, which is what shows
        // Stop and keeps Save and Publish available. Conversation and Manual
        // Start stay on the normal workflow-run / workflow-stop cycle.
        if (messageEvent && launchedProjectId) {
          try {
            sessionStorage.setItem(`sa-workflow-pod:${launchedProjectId}`, 'Running');
          } catch (error) {
            /* ignore storage failures */
          }
          window.dispatchEvent(
              new CustomEvent('workflow-pod-status', {
                detail: { status: 'Running', messageEvent: true },
              })
          );
          setWorkloadWatchId(launchedProjectId);
        }
        const agentNode = currentNodes.find(n => n.type === 'agent');
        window.dispatchEvent(new CustomEvent('workflow-run', { detail: { nodeId: agentNode?.id } }));
      }
    }

    async function handleGenerateWorkflow(e) {
      // Get current nodes and edges from ReactFlow instance
      const currentRfInstance = rfInstanceRef.current || rfInstance;
      if (!currentRfInstance) {
        showNotificationGlobal('Workflow component not ready. Please try again.');
        return;
      }

      const currentNodes = currentRfInstance.getNodes();
      const currentEdges = currentRfInstance.getEdges();

      // Validate workflow structure
      const isValid = await validateWorkflowRef.current(currentNodes, currentEdges, (message, type) => showAlert(message, type));
      if (isValid !== true) {
        return;
      }

      // Build payload (same structure as workflow execution)
      let payload;
      try {
        // For generate we don't require a query, pass empty string
        payload = buildWorkflowPayload(currentNodes, currentEdges, '');
      } catch (error) {
        showNotificationGlobal('Error building workflow payload: ' + error.message, 'error');
        return;
      }

      // Call generate workflow API with full payload (same as execute)
      WorkflowService.generateWorkflow(payload)
          .then((res) => {
            if (res?.status === API_RESPONSE.SUCCESS) {
              showNotificationGlobal('Workflow generated successfully', 'success', 'bottomRight');
              // You can handle the response here, e.g., update nodes/edges with generated workflow
            } else {
              const errorMsg = res?.message || 'Unknown error occurred';
              showAlert('Failed to generate workflow: ' + errorMsg, 'error');
            }
          })
          .catch((err) => {
            console.error('Error generating workflow:', err);
            const userMessage = err?.response?.data?.message || err?.message || 'An error occurred while generating the workflow';
            showAlert('Error generating workflow: ' + userMessage, 'error');
          });
    }


    const saveWorkflow = async (
        onComplete,
        saveToBackend = false,
        runworkflowSave = false,
        evaluation = null
    ) => {
      if (isSavingRef.current || isCreatingWorkflowRef.current) {
        return;
      }

      try {
        isSavingRef.current = true;
        setIsSaving(true);

        const waitForRfInstance = (maxWaitTime = 5000, checkInterval = 100) =>
            new Promise((resolve, reject) => {
              const startTime = Date.now();

              const checkInstance = () => {
                const instance = rfInstanceRef.current || rfInstance;

                if (instance) return resolve(instance);

                if (Date.now() - startTime >= maxWaitTime)
                  return reject(new Error('Workflow component initialization timeout'));

                setTimeout(checkInstance, checkInterval);
              };

              checkInstance();
            });

        const currentRfInstance = await waitForRfInstance();

        const currentNodes = currentRfInstance.getNodes();
        const currentEdges = currentRfInstance.getEdges();

        const isValid = await validateWorkflowRef.current(
            currentNodes,
            currentEdges,
            (message, type) => showAlert(message, type)
        );

        if (isValid !== true) {
          setIsSaving(false);
          isSavingRef.current = false;
          if (onComplete) onComplete(false);
          return;
        }
        const payload = buildWorkflowPayload(
            currentNodes,
            currentEdges,
            consoleQueryRef.current
        );

        applyEvaluationToPayload(payload, evaluation || evaluationSettingsRef.current);
        if (evaluation) {
          evaluationSettingsRef.current = evaluation;
          storeEvaluationSettings(ProjectID, evaluation);
        }

        isCreatingWorkflowRef.current = true;

        const res = await WorkflowService.createWorkflow(payload);

        isCreatingWorkflowRef.current = false;

        const isSuccess =
            res?.status === API_RESPONSE.SUCCESS ||
            res?.data?.status === API_RESPONSE.SUCCESS ||
            (res &&
                !res.status &&
                !res.errorMessage &&
                (res.projectDTO || res.projectId || res.id));

        if (!isSuccess) {
          const errorMsg = res?.errorMessage || 'Unknown error occurred';
          showNotificationGlobal(
              evaluation
                  ? 'Failed to save evaluation: ' + errorMsg
                  : 'Failed to create workflow: ' + errorMsg,
              'error'
          );
          setIsSaving(false);
          isSavingRef.current = false;
          if (onComplete) onComplete(false);
          return;
        }

        savedNodesRef.current = JSON.parse(JSON.stringify(currentNodes));
        savedEdgesRef.current = JSON.parse(JSON.stringify(currentEdges));
        savedAppConfigRef.current = JSON.parse(
            JSON.stringify(payload.appConfig)
        );
        markWorkflowClean();

        if (!runworkflowSave) {
          requestAnimationFrame(() => {
            showNotificationGlobal(
                evaluation
                    ? (evaluation.evaluate
                        ? 'Evaluation settings saved'
                        : 'Evaluation disabled')
                    : 'Workflow saved successfully',
                'success',
                'bottomRight'
            );
          });
        }
        setTimeout(() => {
          updateProjectIdFromResponse(res);
          window.dispatchEvent(new CustomEvent('workflow-saved'));
        }, 0);

        setIsSaving(false);
        isSavingRef.current = false;
        if (onComplete) onComplete(true);

      } catch (err) {
        isCreatingWorkflowRef.current = false;
        isSavingRef.current = false;
        console.error('Save workflow error:', err);

        showNotificationGlobal(
            'Error creating workflow: ' +
            (err?.response?.data?.message || err?.message || 'Unexpected error'),
            'error'
        );

        setIsSaving(false);
        if (onComplete) onComplete(false);
      }
    };

    saveWorkflowRef.current = saveWorkflow;

    function handleSaveWorkflow(e) {
      const silent = Boolean(e?.detail?.silent);
      const evaluation = e?.detail?.evaluation || null;
      saveWorkflowRef.current((success) => {
        if (success) {
        }
      }, true, silent, evaluation); // true indicates save to backend, not WebSocket
    }

    function handlePublishWorkflow(e) {
      (async () => {
        try {
          setIsSaving(true);

          const currentRfInstance = rfInstanceRef.current || rfInstance;
          if (!currentRfInstance) {
            showNotificationGlobal('Workflow component not ready. Please try again.');
            setIsSaving(false);
            return;
          }

          const currentNodes = currentRfInstance.getNodes();
          const currentEdges = currentRfInstance.getEdges();

          const isValid = await validateWorkflowRef.current(
              currentNodes,
              currentEdges,
              (message, type) => {
                showAlert(message, type);
              }
          );
          if (isValid !== true) {
            setIsSaving(false);
            return;
          }

          const payload = buildWorkflowPayload(
              currentNodes,
              currentEdges,
              consoleQueryRef.current
          );

          const meta = e?.detail || {};

          const publishName =
              typeof meta?.name === "string" ? meta.name.trim() : "";

          const publishDescription =
              typeof meta?.description === "string"
                  ? meta.description.trim()
                  : "";

          // Update payload name & description
          if (publishName) {
            payload.name = publishName;
          }

          if (publishDescription) {
            payload.description = payload.description || publishDescription;
          }

          // Update appConfig safely
          if (payload.appConfig) {
            if (publishName) {
              payload.appConfig.appName = publishName;
            }

            if (publishDescription) {
              payload.appConfig.description =
                  publishDescription || payload.appConfig.description || "";
            }
          }

          // orchestratorId is the workflow template id
          payload.orchestratorId =
              workfowAppInfo?.workFlowTemplateDTO?.id ||
              workfowAppInfo?.id || ProjectID;

          const res = await WorkflowService.publishWorkflow(payload);

          const isSuccess =
              res?.status === API_RESPONSE.SUCCESS ||
              res?.data?.status === API_RESPONSE.SUCCESS ||
              (res &&
                  !res.status &&
                  !res.errorMessage &&
                  (res.projectDTO || res.projectId || res.id));

          if (!isSuccess) {
            const errorMsg = res?.message || 'Unknown error occurred';
            showNotificationGlobal(
                'Failed to publish workflow: ' + errorMsg,
                'error'
            );
            setIsSaving(false);
            return;
          }

          // Update id refs similarly to save
          savedNodesRef.current = JSON.parse(JSON.stringify(currentNodes));
          savedEdgesRef.current = JSON.parse(JSON.stringify(currentEdges));
          savedAppConfigRef.current = JSON.parse(
              JSON.stringify(payload.appConfig)
          );

          requestAnimationFrame(() => {
            showNotificationGlobal(
                'Workflow published successfully',
                'success',
                'bottomRight'
            );
          });

          setTimeout(() => {
            updateProjectIdFromResponse(res);
            window.dispatchEvent(new CustomEvent('workflow-saved'));
          }, 0);

          setIsSaving(false);
        } catch (err) {
          console.error('Publish workflow error:', err);
          showNotificationGlobal(
              'Error publishing workflow: ' +
              (err?.response?.data?.message || err?.message || 'Unexpected error'),
              'error'
          );
          setIsSaving(false);
        }
      })();
    }

    // Handler to check input trigger type and execute or open the chat window
    function handleCheckAndExecuteWorkflow() {
      const currentRfInstance = rfInstanceRef.current || rfInstance;
      if (!currentRfInstance) {
        showNotificationGlobal('Workflow component not ready. Please try again.');
        return;
      }

      const currentNodes = currentRfInstance.getNodes();
      const inputNode = currentNodes.find(n => n.type === 'input');
      const triggerId = inputNode?.data?.triggerId || inputNode?.data?.triggerType || '';

      if (isWorkflowRunning) {
        if (isMessageEventWorkflow(currentNodes)) {
          window.dispatchEvent(new CustomEvent('stop-workflow-pod'));
        }
        return;
      }

      if (triggerId === 'conversation') {
        window.dispatchEvent(new CustomEvent('open-console-drawer', {
          detail: { focusInput: true }
        }));
        return;
      }

      // Manual Start, Message Event, and every other event call the execute
      // endpoint directly. Conversation waits for a query in the chat window.
      window.dispatchEvent(new CustomEvent('execute-workflow', {
        detail: { query: '' }
      }));
    }

    eventSystem.current.addEventListener('agent-run', handleAgentRun);
    eventSystem.current.addEventListener('agent-stop', handleAgentStop);

    // Add event listener for execute-workflow event from ProjectView button
    window.addEventListener('execute-workflow', handleExecuteWorkflow);

    // Add event listener for check-and-execute-workflow event
    window.addEventListener('check-and-execute-workflow', handleCheckAndExecuteWorkflow);

    // Add event listener for save-workflow event from ProjectView button
    window.addEventListener('save-workflow', handleSaveWorkflow);

    // Allow parent layouts to discard unsaved changes (treat current graph as clean).
    function handleDiscardWorkflowChanges() {
      const currentRfInstance = rfInstanceRef.current || rfInstance;
      if (currentRfInstance) {
        try {
          const currentNodes = currentRfInstance.getNodes();
          const currentEdges = currentRfInstance.getEdges();
          savedNodesRef.current = JSON.parse(JSON.stringify(currentNodes));
          savedEdgesRef.current = JSON.parse(JSON.stringify(currentEdges));
        } catch (_) {
          // best-effort; still mark clean
        }
      }
      markWorkflowClean();
    }
    window.addEventListener('workflow-discard-changes', handleDiscardWorkflowChanges);

    // Add event listener for generate-workflow event from WorkflowHeader button
    window.addEventListener('generate-workflow', handleGenerateWorkflow);

    // Add event listener for publish-workflow event from WorkflowHeader button
    window.addEventListener('publish-workflow', handlePublishWorkflow);

    return () => {
      eventSystem.current.removeEventListener('agent-run', handleAgentRun);
      eventSystem.current.removeEventListener('agent-stop', handleAgentStop);
      window.removeEventListener('execute-workflow', handleExecuteWorkflow);
      window.removeEventListener('check-and-execute-workflow', handleCheckAndExecuteWorkflow);
      window.removeEventListener('save-workflow', handleSaveWorkflow);
      window.removeEventListener('workflow-discard-changes', handleDiscardWorkflowChanges);
      window.removeEventListener('generate-workflow', handleGenerateWorkflow);
      window.removeEventListener('publish-workflow', handlePublishWorkflow);
    };
  }, [isWorkflowRunning, runningAgentId, buildWorkflowPayload, ProjectID, showAlert, rfInstance, markWorkflowClean]);


  // Cleanup event system on unmount
  useEffect(() => {
    return () => {
      eventSystem.current.cleanup();
    };
  }, []);

  // Helper function to check if goal is satisfied (recursive check)
  const checkGoalSatisfied = useCallback((msg, depth = 0) => {
    const MAX_RECURSION_DEPTH = 5;
    if (msg === null || msg === undefined || depth > MAX_RECURSION_DEPTH) return false;

    // Handle arrays
    if (Array.isArray(msg)) {
      for (const item of msg) {
        if (checkGoalSatisfied(item, depth + 1)) return true;
      }
      return false;
    }

    // Handle objects - check for prototype pollution
    if (typeof msg === 'object' && msg.constructor === Object) {
      // Check direct property
      if (msg.is_goal_satisfied === true) {
        return true;
      }

      // Recursively check all object properties
      for (const key in msg) {
        if (Object.prototype.hasOwnProperty.call(msg, key)) {
          // Prevent prototype pollution
          if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
            continue;
          }
          const value = msg[key];
          if (value !== null && value !== undefined && typeof value === 'object') {
            if (checkGoalSatisfied(value, depth + 1)) return true;
          }
        }
      }
    }

    return false;
  }, []);

  // Ref to track setTimeout IDs for cleanup
  const timeoutRefs = useRef(new Set());

  // Helper to add timeout with cleanup tracking
  const addTimeout = useCallback((callback, delay) => {
    const timeoutId = setTimeout(() => {
      timeoutRefs.current.delete(timeoutId);
      callback();
    }, delay);
    timeoutRefs.current.add(timeoutId);
    return timeoutId;
  }, []);

  // Listen to WebSocket messages to track currently running agent in multi-agent workflows based on uid
  useEffect(() => {
    function handleMessageReceived(event) {
      try {
        const message = typeof event.detail === 'string' ? JSON.parse(event.detail) : event.detail;
        // Extract projectID from message and compare with current ProjectID
        const messageProjectId = message?.ninfo?.projectId || message?.projectId || message?.project_id;

        // Only process messages if projectID matches (or if ProjectID is not set yet)
        if (ProjectID && messageProjectId && messageProjectId !== ProjectID) {
          return; // Ignore messages from different projects
        }

        // Handle events from ninfo.msg.event structure
        const eventType = message?.ninfo?.msg?.event;
        if (eventType) {
          const msgData = message.ninfo.msg || {};
          const errorMessage = msgData.error || msgData.message || 'An error occurred';
          // Extract status from msgData - check multiple possible locations
          const status = msgData.status || msgData.state || message?.ninfo?.status || message?.status;
          // Extract reason/result message
          const reason = msgData.error || msgData.reason || msgData.message || msgData.result || msgData.data || 'No details available';

          // Check status first - if FAILED, handle it before event processing
          if (status === 'FAILED' || status === 'failed' || status === 'ERROR' || status === 'error') {
            // Stop all highlighting
            window.dispatchEvent(new CustomEvent('node-highlight-stop-all'));
            window.dispatchEvent(new CustomEvent('agent-stop-all'));

            // Find the relevant node based on event type
            let targetNodeId = null;
            let targetNodeType = null;

            if (eventType === 'request_execute' || eventType === 'request_failed') {
              const inputNode = nodes.find(n => n.type === 'input');
              if (inputNode) {
                targetNodeId = inputNode.id;
                targetNodeType = 'input';
              }
            } else if (eventType.includes('agent')) {
              const agentId = msgData.agentId;
              if (agentId) {
                const agentUid = msgData.requestId || agentId;
                const nodeId = agentUidToNodeIdMap.current.get(agentUid) ||
                    nodes.find(n => n.type === 'agent' && (n.data?.agentId === agentId || n.id === agentId))?.id;
                if (nodeId) {
                  targetNodeId = nodeId;
                  targetNodeType = 'agent';
                }
              }
            }

            // Dispatch status tooltip event with reason
            if (targetNodeId) {
              window.dispatchEvent(new CustomEvent('node-status-tooltip', {
                detail: {
                  nodeId: targetNodeId,
                  nodeType: targetNodeType,
                  status: 'FAILED',
                  reason: reason
                }
              }));
            }

            // Stop workflow and show error in alert
            setRunningAgentId(null);
            setIsWorkflowRunning(false);
            window.dispatchEvent(new CustomEvent('workflow-stop'));
            showAlert(errorMessage || 'Workflow execution failed', 'error', reason || errorMessage || 'Workflow execution failed');
            console.error('Workflow Failed:', errorMessage);

            // Dispatch workflow event
            window.dispatchEvent(new CustomEvent('workflow-event', {
              detail: {
                eventType: 'request_failed',
                errorMessage: errorMessage
              }
            }));
            return;
          }

          // Check for SUCCESS status - we'll handle tooltip after processing events
          const isSuccess = status === 'SUCCESS' || status === 'success' || status === 'COMPLETED' || status === 'completed';

          // Stop all highlighting first (for non-FAILED cases)
          window.dispatchEvent(new CustomEvent('node-highlight-stop-all'));

          // Handle different event types
          switch (eventType) {
            case 'request_execute':
              // Only highlight if status is not FAILED (already handled above)
              if (status !== 'FAILED' && status !== 'failed' && status !== 'ERROR' && status !== 'error') {
                // Dispatch workflow event for status indicator
                window.dispatchEvent(new CustomEvent('workflow-event', {
                  detail: { eventType: 'request_execute' }
                }));
                // Highlight input node
                const inputNode = nodes.find(n => n.type === 'input');
                if (inputNode) {
                  window.dispatchEvent(new CustomEvent('node-highlight', {
                    detail: { nodeId: inputNode.id, nodeType: 'input', color: 'green' }
                  }));
                }
              }
              break;

            case 'agent_goal_satisfied':
              // When goal is satisfied, stop agent highlighting but keep workflow running until request_completed
              setRunningAgentId(null);
              window.dispatchEvent(new CustomEvent('agent-stop-all'));
              break;

            case 'request_completed':
              // Extract result from message - check multiple possible locations
              // First check msgData (message.ninfo.msg), including 'results' (plural)
              let resultData = msgData.results || msgData.result || msgData.response || msgData.data || msgData.output || msgData.content || msgData.answer || msgData.message;

              // Also check the full message structure for result
              if (!resultData && message) {
                resultData = message.results || message.result || message.response || message.data || message.output || message.content || message.answer || message.message;
              }

              // Also check message.ninfo structure (excluding msg which we already checked)
              if (!resultData && message?.ninfo) {
                resultData = message.ninfo.results || message.ninfo.result || message.ninfo.response || message.ninfo.data || message.ninfo.output || message.ninfo.content;
              }
              // Extract the actual result text from nested structure
              let extractedResult = null;
              if (resultData) {
                // If resultData is an array, get the first item
                if (Array.isArray(resultData) && resultData.length > 0) {
                  const firstItem = resultData[0];
                  // Extract 'result' field from the item, or use 'reason' as fallback
                  extractedResult = firstItem.result || firstItem.reason || firstItem.data || firstItem;
                }
                // If resultData is an object, check for 'result' field first, then 'data'
                else if (typeof resultData === 'object' && resultData !== null) {
                  extractedResult = resultData.result || resultData.data || resultData.reason || resultData;
                }
                else {
                  extractedResult = resultData;
                }
              } else {
              }

              // Format result for display - show only the result text, not the entire JSON
              let formattedResult = 'Query executed successfully';
              if (extractedResult) {
                if (typeof extractedResult === 'object' && extractedResult !== null) {
                  // If it's still an object, try to get a string value
                  if (extractedResult.result) {
                    formattedResult = String(extractedResult.result);
                  } else if (extractedResult.reason) {
                    formattedResult = String(extractedResult.reason);
                  } else if (extractedResult.data) {
                    formattedResult = String(extractedResult.data);
                  } else {
                    formattedResult = JSON.stringify(extractedResult, null, 2);
                  }
                } else {
                  formattedResult = String(extractedResult);
                }
              }

              // For SUCCESS status, show alert and tooltip
              if (isSuccess) {
                // Extract reason from results if available
                let successReason = formattedResult || 'Query executed successfully';
                if (resultData && Array.isArray(resultData) && resultData.length > 0) {
                  const firstItem = resultData[0];
                  // Prefer reason field, then result, then formattedResult
                  successReason = firstItem.reason || firstItem.result || formattedResult || 'Query executed successfully';
                } else if (resultData && typeof resultData === 'object' && resultData.reason) {
                  successReason = resultData.reason || formattedResult || 'Query executed successfully';
                }
                // Output node removed - tooltip functionality no longer needed
              }

              // Dispatch workflow event for status indicator with result
              window.dispatchEvent(new CustomEvent('workflow-event', {
                detail: {
                  eventType: 'request_completed',
                  result: formattedResult
                }
              }));
              // Stop workflow and update button state (no highlighting needed - already highlighted on goal_satisfied)
              setRunningAgentId(null);
              setIsWorkflowRunning(false);
              window.dispatchEvent(new CustomEvent('agent-stop-all'));
              window.dispatchEvent(new CustomEvent('workflow-stop'));
              break;

            case 'agent_execute_start':
              // Only highlight if status is not FAILED
              if (status !== 'FAILED' && status !== 'failed' && status !== 'ERROR' && status !== 'error') {
                // Dispatch workflow event for status indicator
                window.dispatchEvent(new CustomEvent('workflow-event', {
                  detail: { eventType: 'agent_execute_start' }
                }));
              }
              break;

            case 'agent_execute':
            case 'agent_execute_complete':
              // Only highlight if status is not FAILED
              if (status !== 'FAILED' && status !== 'failed' && status !== 'ERROR' && status !== 'error') {
                // Dispatch workflow event for status indicator
                window.dispatchEvent(new CustomEvent('workflow-event', {
                  detail: { eventType: eventType }
                }));
                // Keep agent highlighted during execution and completion
                const executingAgentId = msgData.agentId;
                if (executingAgentId) {
                  const node = nodes.find(
                      n =>
                          (n.type === 'agent' || n.type === 'task') &&
                          (n.data?.agentId === executingAgentId || n.id === executingAgentId)
                  );
                  const nodeId = node?.id;
                  const nodeType = node?.type;
                  if (nodeId) {
                    // Highlight the primary executing agent node
                    window.dispatchEvent(new CustomEvent('node-highlight', {
                      detail: { nodeId: nodeId, nodeType: nodeType, color: 'green' }
                    }));

                    // Also highlight any parallel agents that share the same source node
                    const incomingEdge = edges.find(e => e.target === nodeId);
                    if (incomingEdge && incomingEdge.source) {
                      const sharedSourceId = incomingEdge.source;
                      edges
                          .filter(e => e.source === sharedSourceId && e.target !== nodeId)
                          .forEach(e => {
                            const parallelNode = nodes.find(
                                n =>
                                    n.id === e.target &&
                                    (n.type === 'agent' || n.type === 'task')
                            );
                            if (parallelNode) {
                              window.dispatchEvent(new CustomEvent('node-highlight', {
                                detail: {
                                  nodeId: parallelNode.id,
                                  nodeType: parallelNode.type,
                                  color: 'green'
                                }
                              }));
                            }
                          });
                    }

                    // For SUCCESS status on agent completion, show tooltip
                    if (isSuccess && eventType === 'agent_execute_complete') {
                      window.dispatchEvent(new CustomEvent('node-status-tooltip', {
                        detail: {
                          nodeId: nodeId,
                          nodeType: nodeType,
                          status: 'SUCCESS',
                          reason: reason || 'Agent execution completed successfully'
                        }
                      }));
                    }
                  }
                }
              }
              break;

            case 'agent_llm_execute':
              // Highlight LLM node connected to the executing agent
              const llmAgentId = msgData.agentId;
              let llmNode = null;
              if (llmAgentId) {
                // Find agent node first
                const llmAgentUid = msgData.requestId || llmAgentId;
                const llmAgentNodeId = agentUidToNodeIdMap.current.get(llmAgentUid) ||
                    nodes.find(n => n.type === 'agent' && (n.data?.agentId === llmAgentId || n.id === llmAgentId))?.id;
                if (llmAgentNodeId) {
                  // Find LLM node connected to this agent
                  llmNode = nodes.find(n =>
                      n.type === 'config' &&
                      n.data?.configType === 'llm' &&
                      edges.some(e => e.source === llmAgentNodeId && e.target === n.id)
                  );
                }
              }
              // Fallback to first LLM node if no specific connection found
              if (!llmNode) {
                llmNode = nodes.find(n => n.type === 'config' && n.data?.configType === 'llm');
              }
              if (llmNode) {
                window.dispatchEvent(new CustomEvent('node-highlight', {
                  detail: { nodeId: llmNode.id, nodeType: 'config', color: 'green' }
                }));
              }
              break;

            case 'agent_execute_error':
            case 'agent_execute_timeout':
            case 'agent_execute_cancelled':
            case 'agent_execute_failed':
              // Dispatch workflow event for status indicator with error message
              window.dispatchEvent(new CustomEvent('workflow-event', {
                detail: {
                  eventType: eventType,
                  errorMessage: errorMessage || `Workflow Error (${eventType})`
                }
              }));
              // Highlight agent node in red
              const errorAgentId = msgData.agentId;
              if (errorAgentId) {
                const errorAgentUid = msgData.requestId || errorAgentId;
                const errorNodeId = agentUidToNodeIdMap.current.get(errorAgentUid) ||
                    nodes.find(n => n.type === 'agent' && (n.data?.agentId === errorAgentId || n.id === errorAgentId))?.id;
                if (errorNodeId) {
                  window.dispatchEvent(new CustomEvent('node-highlight', {
                    detail: { nodeId: errorNodeId, nodeType: 'agent', color: 'red' }
                  }));
                }
              }
              // Stop workflow and show error in alert
              setRunningAgentId(null);
              setIsWorkflowRunning(false);
              window.dispatchEvent(new CustomEvent('agent-stop-all'));
              window.dispatchEvent(new CustomEvent('workflow-stop'));
              // Show error in alert with tooltip
              break;

            case 'request_failed':
              // Dispatch workflow event for status indicator with error message
              window.dispatchEvent(new CustomEvent('workflow-event', {
                detail: {
                  eventType: 'request_failed',
                  errorMessage: errorMessage || 'Workflow execution failed'
                }
              }));
              // Highlight in red and stop workflow
              const failedInputNode = nodes.find(n => n.type === 'input');
              if (failedInputNode) {
                window.dispatchEvent(new CustomEvent('node-highlight', {
                  detail: { nodeId: failedInputNode.id, nodeType: 'input', color: 'red' }
                }));
              }
              // Stop workflow and show error in alert
              setRunningAgentId(null);
              setIsWorkflowRunning(false);
              window.dispatchEvent(new CustomEvent('agent-stop-all'));
              window.dispatchEvent(new CustomEvent('workflow-stop'));
              // Show error in alert with tooltip
              showAlert(errorMessage || 'Workflow execution failed', 'error', reason || errorMessage || 'Workflow execution failed');
              console.error('Workflow Failed:', errorMessage);
              break;

            case 'result_execute':
              // Dispatch workflow event for status indicator
              window.dispatchEvent(new CustomEvent('workflow-event', {
                detail: { eventType: 'result_execute' }
              }));
              break;

            case 'Pipe_flow_start':
              // Dispatch workflow event for status indicator
              window.dispatchEvent(new CustomEvent('workflow-event', {
                detail: { eventType: 'Pipe_flow_start' }
              }));
              break;

            default:
              // Unknown event type, continue with existing logic
              break;
          }

          // Return early if we handled the event
          return;
        }
        // Extract uid from various possible message formats
        // Common patterns: message.uid, message.agentUid, message.data.uid, message.result.uid
        const agentUid = message?.uid || message?.agentUid || message?.agent?.uid ||
            message?.data?.uid || message?.data?.agentUid ||
            message?.result?.uid || message?.result?.agentUid ||
            message?.agent?.data?.uid || message?.agent?.data?.agentUid;

        // Extract status from various possible locations
        const status = message?.status || message?.state || message?.result?.status ||
            message?.agent?.status || message?.data?.status;

        // Check if message indicates agent execution is running
        const isExecuting = status === 'running' || status === 'executing' || status === 'processing' ||
            status === 'active' || message?.executing || message?.running ||
            message?.isRunning === true;

        // Check if message indicates workflow/agent completion
        const isCompleted = status === 'completed' || status === 'success' || status === 'finished' ||
            status === 'done' || message?.completed || message?.finished ||
            message?.isCompleted === true;

        // If workflow is completed and we have result data, extract and dispatch it
        if (isCompleted && isWorkflowRunning) {
          // Check for result in various locations
          let resultData = message?.result || message?.results || message?.response || message?.data ||
              message?.output || message?.content || message?.answer ||
              message?.ninfo?.result || message?.ninfo?.results || message?.ninfo?.msg?.result || message?.ninfo?.msg?.results;

          if (resultData) {
            // Extract the actual result text
            let extractedResult = null;
            if (Array.isArray(resultData) && resultData.length > 0) {
              const firstItem = resultData[0];
              extractedResult = firstItem.result || firstItem.reason || firstItem.data || firstItem;
            } else if (typeof resultData === 'object' && resultData !== null) {
              extractedResult = resultData.result || resultData.data || resultData.reason || resultData;
            } else {
              extractedResult = resultData;
            }

            // Format result
            let formattedResult = null;
            if (extractedResult) {
              if (typeof extractedResult === 'object' && extractedResult !== null) {
                formattedResult = extractedResult.result || extractedResult.reason || extractedResult.data ||
                    JSON.stringify(extractedResult, null, 2);
              } else {
                formattedResult = String(extractedResult);
              }

              // Dispatch workflow event with result if we have a formatted result
              if (formattedResult && formattedResult !== 'Query executed successfully') {
                window.dispatchEvent(new CustomEvent('workflow-event', {
                  detail: {
                    eventType: 'request_completed',
                    result: formattedResult
                  }
                }));
              }
            }
          }
        }

        // If we have a uid and agent is executing
        if (agentUid && isExecuting && isWorkflowRunning) {
          // Map uid to node.id
          const nodeId = agentUidToNodeIdMap.current.get(agentUid);

          if (nodeId) {
            // If this is a different agent, stop all first
            if (nodeId !== runningAgentId) {
              // Stop ALL agents first to ensure only one is highlighted
              window.dispatchEvent(new CustomEvent('agent-stop-all'));

              // Update running agent ID to highlight the currently executing agent
              setRunningAgentId(nodeId);

              // Use setTimeout to ensure stop-all event is processed before run event
              addTimeout(() => {
                window.dispatchEvent(new CustomEvent('agent-run', {
                  detail: { nodeId: nodeId }
                }));
              }, runningAgentId !== null ? 150 : 0);
            } else if (nodeId === runningAgentId) {
              // Same agent is still running - ensure it's highlighted
              // This handles cases where the event might have been missed
              window.dispatchEvent(new CustomEvent('agent-run', {
                detail: { nodeId: nodeId }
              }));
            }
          } else {
            // If nodeId is not found in map, log for debugging
            console.warn('DrawWorkFlow: Agent UID not found in map:', agentUid, 'Available mappings:', Array.from(agentUidToNodeIdMap.current.entries()));
          }
        }

        // If workflow/agent is completed, clear the running agent
        if (isCompleted && runningAgentId) {
          // Only clear if the completed agent matches the currently running one (by checking uid)
          if (agentUid) {
            const completedNodeId = agentUidToNodeIdMap.current.get(agentUid);
            if (completedNodeId === runningAgentId) {
              // Clear running agent highlighting
              setRunningAgentId(null);
              setIsWorkflowRunning(false);
              window.dispatchEvent(new CustomEvent('agent-stop', {
                detail: { nodeId: runningAgentId }
              }));
              // Dispatch workflow-stop event to update button state
              window.dispatchEvent(new CustomEvent('workflow-stop'));
            }
          } else {
            // If no uid in completion message, clear if workflow is fully completed
            if (status === 'completed' || status === 'success' || status === 'finished') {
              setRunningAgentId(null);
              setIsWorkflowRunning(false);
              window.dispatchEvent(new CustomEvent('agent-stop', {
                detail: { nodeId: runningAgentId }
              }));
              // Dispatch workflow-stop event to update button state
              window.dispatchEvent(new CustomEvent('workflow-stop'));
            }
          }
        }

        // Also check for workflow completion when isWorkflowRunning is true but no specific agent is running
        // This handles cases where the workflow completes without a specific agent ID
        if (isCompleted && isWorkflowRunning && !runningAgentId) {
          setIsWorkflowRunning(false);
          window.dispatchEvent(new CustomEvent('workflow-stop'));
        }
      } catch (error) {
        // Silently ignore parsing errors - message might not be JSON or might not contain agent info
        console.debug('WebSocket message parsing (non-critical):', error);
      }
    }

    window.addEventListener('messageReceived', handleMessageReceived);
    return () => {
      window.removeEventListener('messageReceived', handleMessageReceived);
      // Cleanup all pending timeouts
      timeoutRefs.current.forEach(timeoutId => clearTimeout(timeoutId));
      timeoutRefs.current.clear();
    };
  }, [isWorkflowRunning, runningAgentId, checkGoalSatisfied, addTimeout, ProjectID, nodes, agentUidToNodeIdMap, setRunningAgentId, setIsWorkflowRunning, showAlert]);

  const handleNodeConfigSave = (data) => {
    setNodes(nds => nds.map(n => {
      if (n.id === selectedNode.id) {
        return { ...n, data };
      }
      return n;
    }));
    setSelectedNode(prev => prev && { ...prev, data });
  };

  // Initialize default workflow if no appInfo is provided also save appconfig
  useEffect(() => {
    if (!workfowAppInfo && nodes.length === 0) {
      setNodes([]);
      setEdges([]);
    }
    if(workfowAppInfo?.appConfigDTOList && workfowAppInfo?.appConfigDTOList?.length > 0) {
      const payload = buildWorkflowPayload(workfowAppInfo?.appConfigDTOList[0].nodes, workfowAppInfo?.appConfigDTOList[0].edges, null);
      savedAppConfigRef.current = JSON.parse(JSON.stringify(payload.appConfig));
    }
  }, [workfowAppInfo, nodes.length]);

  // Force update nodes and edges when appInfo changes
  useEffect(() => {
    setWorkflowName(workfowAppInfo?.name || '');
    setWorkflowDescription(workfowAppInfo?.description || workfowAppInfo?.projectInfo?.description || '')
    if (workfowAppInfo?.appConfigDTOList?.length > 0) {
      const initialNodes = workfowAppInfo.appConfigDTOList[0].nodes || [];
      const initialEdges = workfowAppInfo.appConfigDTOList[0].edges || [];

      // Remove legacy "output" nodes (Output nodes have been removed from the workflow).
      // Older saved workflows may still contain them; without a nodeType mapping they can
      // render as a tiny dot and still be clickable.
      const legacyRemovedNodeIds = new Set(
          initialNodes.filter(n => n?.type === 'output').map(n => n.id)
      );
      const filteredInitialNodes = initialNodes.filter(n => n?.type !== 'output');
      const filteredInitialEdges = initialEdges.filter(e =>
          !legacyRemovedNodeIds.has(e.source) && !legacyRemovedNodeIds.has(e.target)
      );

      // Process nodes to ensure they have all required properties
      const processedNodes = filteredInitialNodes.map(node => {
        if (node.type === 'agent' || node.type === 'task') {
          return {
            ...node,
            data: {
              ...node.data,
              tools: node.data?.tools || [],
              llm: node.data?.llm || '',
              memory: node.data?.memory || false,
              onConfigNodeAdd: (configType, value) => handleConfigNodeAdd(node.id, configType, value),
              onChange: (data) => handleNodeConfigChange(data),
              selected: false,
            }
          };
        } else if (node.type === 'config' && node.data?.configType === 'tool') {
          const toolConfig = toolNodes.find(t => t.handlerName === node.data.handlerName);
          return {
            ...node,
            data: {
              ...node.data,
              displayName: node.data.displayName || toolConfig?.displayName || node.data.handlerName,
              srcPath: node.data.srcPath || toolConfig?.srcPath,
              icon: node.data.icon || toolConfig?.icon,
              iconColor: node.data.iconColor || toolConfig?.iconColor,
              color: node.data.color || toolConfig?.color,
              attributes: node.data.attributes || {}
            }
          };
        } else if (node.type === 'config' && node.data?.configType === 'llm') {
          const llmConfig = llmNodes.find(l => l.llmType === node.data.llmType);
          const isCustomLlm = isCustomLlmType(node.data.llmType);
          const {
            apiKey: _apiKey,
            apiVersion: _apiVersion,
            asyncMode: _asyncMode,
            embedModel: _embedModel,
            showApiKey: _showApiKey,
            ...restData
          } = node.data || {};
          return {
            ...node,
            data: isCustomLlm
              ? {
                  ...restData,
                  llmType: 'custom',
                  displayName: node.data.displayName || llmConfig?.displayName || 'Custom LLM',
                  model: node.data.model || '',
                  baseUrl: node.data.baseUrl || '',
                }
              : {
                  ...node.data,
                  displayName: node.data.displayName || llmConfig?.displayName || node.data.llmType,
                  model: node.data.model || '',
                  apiKey: node.data.apiKey || '',
                  baseUrl: node.data.baseUrl || '',
                  apiVersion: node.data.apiVersion || '',
                  asyncMode: node.data.asyncMode || '',
                  embedModel: node.data.embedModel || ''
                }
          };
        }
        return node;
      });

      // Process edges to add sourceType and targetType information
      const processedEdges = filteredInitialEdges.map(edge => {
        const sourceNode = processedNodes.find(n => n.id === edge.source);
        const targetNode = processedNodes.find(n => n.id === edge.target);
        return {
          ...edge,
          data: {
            ...edge.data,
            sourceType: sourceNode?.type,
            targetType: targetNode?.type
          }
        };
      });

      setNodes(processedNodes);
      setEdges(processedEdges);
    }
  }, [workfowAppInfo]);

  // filter out any legacy "output" nodes that might exist in the current state
  useEffect(() => {
    const hasOutputNodes = nodes.some(n => n?.type === 'output');
    if (hasOutputNodes) {
      const outputNodeIds = new Set(
          nodes.filter(n => n?.type === 'output').map(n => n.id)
      );

      // Remove output nodes
      const filteredNodes = nodes.filter(n => n?.type !== 'output');

      // Remove edges connected to output nodes
      const filteredEdges = edges.filter(e =>
          !outputNodeIds.has(e.source) && !outputNodeIds.has(e.target)
      );

      setNodes(filteredNodes);
      setEdges(filteredEdges);
    }
  }, [nodes, edges]);

  return (
      <div className={`d-flex flex-column workflow-main-container ${workfowAppInfo?.appConfigDTOList ? "h90" : "h93"}`}>
        {/* Alert banner removed - now shown in WorkflowHeader */}

        {nodes?.length === 0 && <EmptyWorkflow />}

        {/* Toggle button for NodePalette */}
        {nodes?.length > 0 && <div
            onClick={(e) => {
              e.stopPropagation();
              setShowPalette((prev) => !prev)
            }}
            className="palette-toggle-wrapper"
            style={{ left: showPalette ? 204 : 4 }}
        >
          <button className="palette-toggle-button">
            <i
                className={`fa-solid fa-chevron-${showPalette ? 'left' : 'right'} palette-toggle-icon`}
            ></i>
          </button>
        </div>}

        {/* Name/description/save section and workflow canvas (scrollable) */}
        {nodes?.length > 0 && <div className="workflow-content-container">
          {/* NodePalette panel with show/hide logic */}
          <div
              className="node-palette-container-wrapper"
              style={{
                width: showPalette ? 220 : 0,
                minWidth: showPalette ? 220 : 0,
                maxWidth: showPalette ? 220 : 0,
                borderRight: showPalette ? '1px solid #eee' : 'none',
                overflowY: showPalette ? 'auto' : 'hidden',
              }}
          >
            <NodePalette />
          </div>
          <div className="workflow-canvas-wrapper">
            {/* Workflow canvas and drawer */}
            <div ref={reactFlowWrapper} className="workflow-canvas-main workflow-canvas-container" title="">
              {nodes?.length > 0 && <ReactFlow
                  nodes={nodes.filter(n => n?.type !== 'output').map(n => {
                    // Add title based on node type
                    let nodeTitle = '';
                    if (n.type === 'input') {
                      nodeTitle = 'Input Node';
                    } else if (n.type === 'agent') {
                      nodeTitle = 'Agent Node';
                    } else if (n.type === 'config') {
                      if (n.data?.configType === 'tool') {
                        nodeTitle = `Tool: ${n.data?.displayName || 'Tool'}`;
                      } else if (n.data?.configType === 'llm') {
                        nodeTitle = `LLM: ${n.data?.displayName || 'LLM'}`;
                      } else {
                        nodeTitle = `Config: ${n.data?.configType || 'Config'}`;
                      }
                    } else {
                      nodeTitle = `${n.type.charAt(0).toUpperCase() + n.type.slice(1)} Node`;
                    }

                    if (invalidAgentIds.includes(n.id)) {
                      // Still compute hasConnection and connectedConfig for invalid agents
                      // This ensures proper state even when there are errors
                      const hasConnection = edges.some(e => e.source === n.id && e.sourceHandle === 'agentOutput');
                      const connectedConfig = agentConnections[n.id] || { tool: [], llm: false, memory: false };

                      return {
                        ...n,
                        title: nodeTitle,
                        style: {
                          ...(n.style || {}),
                          border: '1px solid #dc3545',
                          borderRadius: 14,
                          boxShadow: '0 0 0 4px #ffe0b2, 0 0 8px 2px orange',
                          background: '#fff8e1',
                          position: 'absolute',
                          animation: 'pulse-highlight 1.4s infinite',
                        },
                        data: {
                          ...n.data,
                          errorMessage: 'This agent is validate a Tool or LLM connection.',
                          hasConnection, // Set hasConnection properly
                          connectedConfig, // Set connectedConfig properly
                        }
                      };
                    }
                    // Inject hasConnection for input nodes
                    if (n.type === 'input') {
                      const hasConnection = edges.some(e => e.source === n.id || e.target === n.id);
                      return {
                        ...n,
                        title: nodeTitle,
                        data: {
                          ...n.data,
                          hasConnection,
                        }
                      };
                    }
                    if (n.type === 'agent') {
                      const hasConnection = edges.some(e => e.source === n.id && e.sourceHandle === 'agentOutput');

                      // Always use the latest computed agentConnections from current edges
                      // This ensures the plus buttons disappear immediately when connections are made
                      const connectedConfig = agentConnections[n.id] || { tool: [], llm: false, memory: false };

                      return {
                        ...n,
                        title: nodeTitle,
                        data: {
                          ...n.data,
                          hasConnection,
                          connectedConfig, // Always use the latest computed connections
                        }
                      };
                    }
                    if (n.type === 'task') {
                      const hasConnection = edges.some(e => e.source === n.id && e.sourceHandle === 'taskOutput');

                      // Always use the latest computed agentConnections from current edges
                      // This ensures the plus buttons disappear immediately when connections are made
                      const connectedConfig = agentConnections[n.id] || { tool: [], llm: false, memory: false };

                      return {
                        ...n,
                        title: nodeTitle,
                        data: {
                          ...n.data,
                          hasConnection,
                          connectedConfig, // Always use the latest computed connections
                        }
                      };
                    }
                    return {
                      ...n,
                      title: nodeTitle
                    };
                  })}
                  edges={edges.map(edge => {
                    return {
                      ...edge,
                      data: {
                        ...edge.data,
                        onEdgeDelete: handleEdgeDelete,
                        selected: edge.selected || false,
                        targetExecuting: executingNodeIds.has(edge.target)
                      }
                    };
                  })}
                  onNodesChange={handleNodesChange}
                  onEdgesChange={handleEdgesChange}
                  onConnect={onConnect}
                  onNodeClick={onNodeClick}
                  onEdgeClick={onEdgeClick}
                  nodeTypes={nodeTypes}
                  edgeTypes={edgeTypes}
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  onPaneClick={onPaneClick}
                  minZoom={0.1}
                  className="react-flow-container"
                  onInit={handleInit}
                  proOptions={proOptions}
              >
                <Controls position="bottom" showFitView />
                <MiniMap position="bottom-right" />
                <Background />
              </ReactFlow>}


              {/* Console Drawer */}
              <ConsoleDrawer
                  isOpen={consoleDrawerOpen}
                  onClose={() => {
                    setConsoleDrawerOpen(false);
                    setFocusConsoleInput(false);
                    setExpandConsoleOnOpen(false);
                    // Dispatch event to notify that console drawer is closed
                    window.dispatchEvent(new CustomEvent('close-console-drawer'));
                  }}
                  nodeData={consoleNodeData?.nodeData}
                  projectId={ProjectID}
                  nodeId={consoleNodeData?.nodeId}
                  sidebarOpen={drawerOpen}
                  paletteOpen={showPalette}
                  focusInput={focusConsoleInput}
                  chatVisible={chatVisible}
                  expandOnOpen={expandConsoleOnOpen}
                  onExpandComplete={() => setExpandConsoleOnOpen(false)}
                  chatWidth={chatWidth}
              />

              {/* Drawer for node config */}
              {selectedNode && <div
                  className="node-config-drawer"
                  style={{
                    top: workfowAppInfo?.id ? 52 : 45,
                    right: drawerOpen ? -8 : -430,
                    height: `calc(100vh - ${workfowAppInfo?.id ? 52 : 45}px)`,
                    boxShadow: drawerOpen ? '0 4px 24px rgba(0,0,0,0.12)' : 'none',
                  }}
              >
                <div className="node-config-drawer-header">
                  <div className="node-config-drawer-header-content">
                    {selectedNode?.type === 'config' && selectedNode.data.configType === 'llm' && (
                        <span className="node-config-drawer-icon">
                      <i className="fa-solid fa-brain"></i>
                    </span>
                    )}
                    <span style={{ fontWeight: 600, fontSize: 15, color: '#2d3748' }}>
                    {selectedNode?.type === 'input' ? 'Input Configuration' :
                        selectedNode?.type === 'config' ?
                            `${selectedNode.data.configType === 'llm' ? 'LLM' :
                                selectedNode.data.configType === 'tool' ? 'Tool' :
                                    selectedNode.data.configType === 'task' ? 'Task' :
                                        selectedNode.data.configType} Configuration` :
                            'Agent Configuration'}
                  </span>
                  </div>
                  <button
                      onClick={closeDrawer}
                      className="node-config-drawer-close-btn"
                  >
                    <i className="fa-solid fa-xmark node-config-drawer-close-icon"></i>
                  </button>
                </div>
                <div className="node-config-drawer-content">
                  <NodeConfigSidebar node={selectedNode} onChange={handleNodeConfigChange} llmNodes={llmNodes} nodes={nodes} edges={edges} />
                </div>
              </div>}
            </div>
          </div>
        </div>}
        <SAModalConfirm
            show={showSWConfirm}
            title="Clear Workflow"
            message={`Are you sure want to clear workflow?`}
            okText="Yes"
            cancelText="No"
            handleOk={() => {
              setShowSWConfirm(false);
              setNodes([]);
              setEdges([]);
              // Reset saved state when clearing via modal
              savedNodesRef.current = [];
              savedEdgesRef.current = [];
            }}
            handleCancel={() => setShowSWConfirm(false)}
        />
        <SAModalConfirm
            show={showDeleteNodeConfirm}
            title={pendingDeleteNode?.nodeType === 'task' ? 'Delete Task Agent' : 'Delete Agent'}
            message={
              pendingDeleteNode?.nodeType === 'task'
                  ? 'Are you sure you want to delete this task agent? Connected tool nodes will also be removed.'
                  : 'Are you sure you want to delete this agent? Connected LLM and tool nodes will also be removed.'
            }
            okText="Yes"
            cancelText="No"
            handleOk={() => {
              if (pendingDeleteNode?.nodeId) {
                performAgentOrTaskDelete(pendingDeleteNode.nodeId);
              }
              setShowDeleteNodeConfirm(false);
              setPendingDeleteNode(null);
            }}
            handleCancel={() => {
              setShowDeleteNodeConfirm(false);
              setPendingDeleteNode(null);
            }}
        />
        {/* Pulse animation for highlight */}
        <style>{`
        @keyframes pulse-highlight {
          0% { box-shadow: 0 0 0 4px #ffcdd2, 0 0 8px 2px #e53935; }
          50% { box-shadow: 0 0 0 8px #ffcdd2, 0 0 16px 4px #e53935; }
          100% { box-shadow: 0 0 0 4px #ffcdd2, 0 0 8px 2px #e53935; }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* Edge button styles */
        .edge-button {
          transition: all 0.2s ease;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
        }
        
        .edge-button:hover {
          transform: scale(1.1);
          filter: drop-shadow(0 4px 8px rgba(0,0,0,0.2));
        }
        
        .edge-button.delete:hover {
          stroke: #495057;
          fill: #f8f9fa;
        }
      `}</style>
      </div>
  );
}
